import {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import {
  ITEM_TYPE_LABELS_FA,
  SERVICE_LABELS_FA,
  WAVE2_BUNDLES,
  type BusinessContextResponse,
  type BusinessInboxItem,
  type CarpetType,
  type CustomerMatchingPreference,
  type IdentityMembershipSummary,
  type MatchingFlowType,
  type OfferSummary,
  type ProfileResponse,
  type ServiceCatalogKey,
  type ServiceRequestItem,
  type ServiceRequestSummary,
  type Wave2Bundle,
  composeAddressWithDetails,
  parseAddressDetails,
} from "@ghalishor/contracts";
import type { VoiceScreen } from "@ghalishor/voice-core";
import { APP_VERSION } from "./version";
import { resolveAppSurface } from "./app-surface";
import { IdentityApiClient, identityErrorMessage } from "./auth/api-client";
import {
  loginWithPasskey,
  loginWithRecoveryCode,
  passkeyUserMessage,
  passkeysSupported,
} from "./auth/passkey";
import { AccountSecurityPanel } from "./auth/account-security-panel";
import { MemoryIdentitySessionStore } from "./auth/session-store";
import {
  clearLoginIntent,
  enforceIdentityRoute,
  IDENTITY_WEB_ROUTES,
  IdentityWebFlow,
  isPlatformWebRoute,
  readLoginIntent,
  resolveIdentityWebRoute,
  routeAfterAuthentication,
  writeLoginIntent,
  type IdentityWebRoute,
  type LoginIntent,
} from "./auth/web-flow";
import "./identity-access.css";
import { AssignmentPanel, formatOfferBadge } from "./assignment-panel";
import {
  BusinessConsoleHub,
  BusinessConsoleLogisticsPage,
  BusinessConsoleRatesPage,
  BusinessConsoleReportsPage,
  BusinessConsoleSettingsPage,
  BusinessConsoleTeamPage,
} from "./business-console";
import { BusinessConsoleFleetPage } from "./business-fleet";
import {
  CustomerHomeHero,
  CustomerRequestList,
  CustomerRequestProgress,
  TodayCalendarCard,
} from "./customer-flow";
import { CustomerMultiRequestWizard as CustomerRequestWizard } from "./customer-multi-request-wizard";
import { CustomerVoiceExpertV3 } from "./customer-voice-expert-v3";
import { CustomerVoiceExpertV3Client } from "./customer-voice-expert-v3-client";
import { BrandLogo, BrandWelcome } from "./brand-logo";
import { PartnersPage } from "./partners-page";
import { DownloadsPage } from "./downloads-page";
import { formatJalaliDateTime } from "./jalali";
import { MessagingCenter } from "./messaging-center";

const PlatformConsole = lazy(async () => {
  const module = await import("./platform/platform-console");
  return { default: module.PlatformConsole };
});

export type Surface = "public" | "customer" | "business" | "platform";

export const surfaceFromPath = (rawPath: string): Surface => {
  const normalizedPath = rawPath.toLowerCase();
  if (normalizedPath.startsWith("/customer")) return "customer";
  if (normalizedPath.startsWith("/business")) return "business";
  if (normalizedPath.startsWith("/platform")) return "platform";
  return "public";
};

const navigate = (route: IdentityWebRoute): void => {
  window.history.pushState({}, "", route);
  window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  window.dispatchEvent(new PopStateEvent("popstate"));
};

/** Footer shortcut to the platform-owner login. */
function FooterBrandSecret() {
  return (
    <footer>
      <span className="footer-brand-domain">ghalishorapp.ir</span>
      <span aria-hidden="true"> · </span>
      <span>هماهنگی حرفه‌ای شستشوی </span>
      <button
        type="button"
        className="footer-brand-secret"
        onClick={() => {
          writeLoginIntent(window.localStorage, "platform");
          window.location.assign(IDENTITY_WEB_ROUTES.loginPlatform);
        }}
      >
        فرش
      </button>
      <span className="footer-version">
        پلتفرم قالیشور · نسخه {APP_VERSION}
      </span>
    </footer>
  );
}

const newIdempotencyKey = (): string =>
  typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const countUnitForItem = (itemType: CarpetType): string => {
  if (itemType === "sofa") return "دست/سرویس";
  if (itemType === "curtain" || itemType === "mattress" || itemType === "other")
    return "عدد";
  return "تخته";
};

const statusLabel = (status: ServiceRequestSummary["status"]): string => {
  switch (status) {
    case "draft":
      return "پیش‌نویس";
    case "submitted":
      return "ارسال‌شده";
    case "matching":
      return "در حال یافتن";
    case "offered":
      return "پیشنهادها آماده";
    case "waiting_acceptance":
      return "منتظر پذیرش";
    case "rejected_by_business":
      return "رد شده";
    case "accepted":
      return "پذیرفته شد";
    case "contact_pending":
      return "هماهنگی تماس";
    case "price_pending":
      return "منتظر تأیید قیمت";
    case "scheduled":
      return "زمان‌بندی شد";
    case "completed":
      return "تکمیل شد";
    case "cancelled":
      return "لغو شده";
    default:
      return status;
  }
};

const isCustomerProfileComplete = (profile: ProfileResponse): boolean =>
  (profile.fullName?.trim().length ?? 0) >= 2 &&
  (profile.city?.trim().length ?? 0) > 0 &&
  (profile.addressText?.trim().length ?? 0) > 0 &&
  profile.addressLat !== null &&
  profile.addressLng !== null;

const normalizeOtpDigits = (value: string): string =>
  value
    .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)))
    .replace(/\D/g, "")
    .slice(0, 6);

type WebOtpListening = {
  readonly controller: AbortController;
  readonly promise: Promise<string | null>;
};

function beginWebOtpListening(): WebOtpListening | null {
  if (!("OTPCredential" in window)) return null;
  const controller = new AbortController();
  type WebOtpCredential = Credential & { readonly code?: string };
  type WebOtpCredentials = CredentialsContainer & {
    get(
      options?: CredentialRequestOptions & {
        readonly otp: { readonly transport: readonly ["sms"] };
        readonly signal: AbortSignal;
      },
    ): Promise<WebOtpCredential | null>;
  };
  const credentials = navigator.credentials as WebOtpCredentials;
  const promise = credentials
    .get({ otp: { transport: ["sms"] }, signal: controller.signal })
    .then((credential) => {
      const code = normalizeOtpDigits(credential?.code ?? "");
      return /^\d{6}$/.test(code) ? code : null;
    })
    .catch(() => null);
  return { controller, promise };
}

const WAVE2_LABELS: Record<Wave2Bundle, string> = {
  faster: "سریع‌تر",
  economical: "اقتصادی",
  higher_guarantee: "ضمانت بالاتر",
};

const WAVE2_DESCRIPTIONS: Record<Wave2Bundle, string> = {
  faster: "اولویت با قالیشویی‌هایی است که زودتر جمع‌آوری و تحویل می‌دهند.",
  economical: "اولویت با پیشنهادهای مقرون‌به‌صرفه‌تر است.",
  higher_guarantee: "اولویت با کیفیت، تخصص و ضمانت خدمات است.",
};

const MATCHING_PREFERENCE_LABELS: Record<CustomerMatchingPreference, string> = {
  balanced: "انتخاب متعادل",
  economical: "قیمت اقتصادی‌تر",
  fastest: "تحویل سریع‌تر",
  quality: "کیفیت و تضمین بیشتر",
};

const MATCHING_FLOW_LABELS: Record<MatchingFlowType, string> = {
  auto_broadcast: "پیشنهاد هوشمند قالیشور",
  curated_offers: "انتخاب از بین پیشنهادها",
};

const ACTIVE_MATCHING_STATUSES = new Set<ServiceRequestSummary["status"]>([
  "submitted",
  "matching",
  "offered",
  "waiting_acceptance",
  "rejected_by_business",
]);

function positiveOrderNumber(value: unknown): number | null {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function orderItemCountLabel(
  item: ServiceRequestSummary["items"][number],
): string {
  const count = positiveOrderNumber(item.count);
  return count === null
    ? "تعداد در حال تکمیل"
    : `${count.toLocaleString("fa-IR")} ${countUnitForItem(item.itemType)}`;
}

function orderItemMeasurementLabel(
  item: ServiceRequestSummary["items"][number],
): string {
  if (item.measurementStatus === "onsite") return "اندازه‌گیری در محل";
  const approximateSqm = positiveOrderNumber(item.approximateSqm);
  return approximateSqm === null
    ? "متراژ در محل مشخص می‌شود"
    : `${approximateSqm.toLocaleString("fa-IR")} مترمربع`;
}

function orderItemServicesLabel(
  item: ServiceRequestSummary["items"][number],
): string {
  const services: readonly unknown[] = Array.isArray(item.services)
    ? item.services
    : [];
  const labels = services
    .map((service) =>
      typeof service === "string" && service in SERVICE_LABELS_FA
        ? SERVICE_LABELS_FA[service as ServiceCatalogKey]
        : null,
    )
    .filter((label): label is string => Boolean(label));
  return labels.length > 0 ? labels.join("، ") : "خدمات در حال تکمیل";
}

function Shell(props: {
  readonly title: React.ReactNode;
  readonly description: string;
  readonly children: React.ReactNode;
  readonly onAccount?: () => void;
  readonly eyebrow?: string;
  readonly immersive?: boolean;
  readonly wide?: boolean;
  readonly showToday?: boolean;
  readonly voiceExpert?: CustomerVoiceExpertV3Client;
  readonly voiceScreen?: VoiceScreen;
  readonly voiceAuthenticated?: boolean;
  readonly voiceGuideAutoSpeak?: boolean;
  readonly voicePermissionPrompt?: boolean;
  readonly onVoiceGuidePlay?: () => void;
  readonly entry?: boolean;
  readonly voiceAfterChildren?: boolean;
}) {
  const voiceGuide =
    props.voiceExpert && props.voiceScreen ? (
      <CustomerVoiceExpertV3
        client={props.voiceExpert}
        screen={props.voiceScreen}
        authenticated={props.voiceAuthenticated ?? true}
        autoSpeak={props.voiceGuideAutoSpeak ?? true}
        permissionPrompt={props.voicePermissionPrompt}
        onPlay={props.onVoiceGuidePlay}
      />
    ) : null;
  const voiceAfterChildren = props.voiceAfterChildren === true;
  return (
    <main
      className={`identity-page${props.immersive ? " immersive" : ""}${props.entry ? " identity-page-entry" : ""}`}
      dir="rtl"
    >
      <header className="identity-nav">
        <BrandLogo compact={!props.entry} />
        {props.onAccount ? (
          <button
            type="button"
            className="link-button"
            onClick={props.onAccount}
          >
            حساب کاربری
          </button>
        ) : null}
      </header>
      <section
        className={`identity-card${props.wide || props.immersive ? " identity-card-wide" : ""}${props.entry ? " identity-card-entry" : ""}`}
      >
        {props.immersive ? null : (
          <>
            {props.showToday ? <TodayCalendarCard compact /> : null}
            {props.eyebrow ? (
              <div className="eyebrow">{props.eyebrow}</div>
            ) : null}
            <h1 className={props.entry ? "entry-hero-title" : undefined}>
              {props.title}
            </h1>
            <p className={`lead${props.entry ? " entry-hero-lead" : ""}`}>
              {props.description}
            </p>
          </>
        )}
        {!voiceAfterChildren ? voiceGuide : null}
        {props.children}
        {voiceAfterChildren ? voiceGuide : null}
      </section>
      <FooterBrandSecret />
    </main>
  );
}

function CustomerEntryHighlights() {
  return (
    <section className="customer-entry-highlights" aria-label="نمونه خدمات">
      <p className="customer-entry-preview-label">نمونه خدمات</p>
      <div className="customer-entry-services" aria-hidden="true">
        <figure>
          <img src="/assets/order-services/carpet.webp" alt="" />
          <figcaption>
            <strong>فرش و قالی</strong>
            <small>ماشینی و دست‌بافت</small>
          </figcaption>
        </figure>
        <figure>
          <img src="/assets/order-services/sofa.webp" alt="" />
          <figcaption>
            <strong>مبل و تشک</strong>
            <small>شستشو در محل</small>
          </figcaption>
        </figure>
        <figure>
          <img src="/assets/order-services/curtain.webp" alt="" />
          <figcaption>
            <strong>پرده و موکِت</strong>
            <small>خدمات تکمیلی</small>
          </figcaption>
        </figure>
      </div>
    </section>
  );
}

function StatusMessage(props: {
  readonly kind: "error" | "info";
  readonly children: React.ReactNode;
}) {
  return (
    <div
      role={props.kind === "error" ? "alert" : "status"}
      className={`message ${props.kind}`}
    >
      {props.children}
    </div>
  );
}

function PasswordField(props: {
  readonly label: string;
  readonly value: string;
  readonly visible: boolean;
  readonly onChange: (value: string) => void;
  readonly onToggle: () => void;
  readonly autoComplete: "current-password" | "new-password";
  readonly required?: boolean;
  readonly minLength?: number;
}) {
  const toggleLabel = props.visible ? "پنهان کردن رمز" : "نمایش رمز";
  return (
    <label>
      {props.label}
      <span className="password-input-wrap">
        <input
          type={props.visible ? "text" : "password"}
          autoComplete={props.autoComplete}
          value={props.value}
          onChange={(event) => props.onChange(event.target.value)}
          minLength={props.minLength}
          required={props.required}
          dir="ltr"
        />
        <button
          type="button"
          className="password-visibility"
          onClick={props.onToggle}
          aria-label={toggleLabel}
          title={toggleLabel}
        >
          {props.visible ? "پنهان" : "نمایش"}
        </button>
      </span>
    </label>
  );
}

export function App() {
  const { sessionStore, identityFlow, api, voiceExpert } = useMemo(() => {
    const store = new MemoryIdentitySessionStore(window.localStorage);
    const apiClient = new IdentityApiClient(window.fetch.bind(window), store);
    return {
      sessionStore: store,
      identityFlow: new IdentityWebFlow(),
      api: apiClient,
      voiceExpert: new CustomerVoiceExpertV3Client((input, authenticated) =>
        apiClient.sendVoiceEvent(input, authenticated),
      ),
    };
  }, []);
  const [pathname, setPathname] = useState(window.location.pathname);
  const [revision, setRevision] = useState(0);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [customerAuthMode, setCustomerAuthMode] = useState<
    "register" | "login" | "forgot"
  >("login");
  const [businessAuthMode, setBusinessAuthMode] = useState<
    "register" | "login" | "forgot"
  >("login");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [showRecoveryLogin, setShowRecoveryLogin] = useState(false);
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [context, setContext] = useState<BusinessContextResponse | null>(null);
  const [loginIntent, setLoginIntent] = useState<LoginIntent | null>(() =>
    readLoginIntent(window.localStorage),
  );
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [profileOnboarding, setProfileOnboarding] = useState(false);
  const profileLocationAttempted = useRef(false);
  const profileNameInput = useRef<HTMLInputElement>(null);
  const profileCityInput = useRef<HTMLInputElement>(null);
  const profileAddressInput = useRef<HTMLTextAreaElement>(null);
  const webOtpListening = useRef<WebOtpListening | null>(null);
  const [profileReturnRoute, setProfileReturnRoute] =
    useState<IdentityWebRoute | null>(null);
  const [fullName, setFullName] = useState("");
  const [city, setCity] = useState("");
  const [addressText, setAddressText] = useState("");
  const [plaque, setPlaque] = useState("");
  const [floorUnit, setFloorUnit] = useState("");
  const [addressLat, setAddressLat] = useState("");
  const [addressLng, setAddressLng] = useState("");
  const [activeJobs, setActiveJobs] = useState<
    readonly import("@ghalishor/contracts").ActiveBusinessJob[]
  >([]);
  const [activeAssignmentId, setActiveAssignmentId] = useState<string | null>(
    null,
  );
  const [bizCity, setBizCity] = useState("");
  const [bizAddress, setBizAddress] = useState("");
  const [bizLat, setBizLat] = useState("");
  const [bizLng, setBizLng] = useState("");
  const [bizRadius, setBizRadius] = useState("15");
  const [bizTypicalReturnDays, setBizTypicalReturnDays] = useState("5");
  const [rateWashing, setRateWashing] = useState("50000");
  const [requests, setRequests] = useState<readonly ServiceRequestSummary[]>(
    [],
  );
  const [editingRequestId, setEditingRequestId] = useState<string | null>(null);
  const [viewingRequestId, setViewingRequestId] = useState<string | null>(null);
  const [platformRequestId, setPlatformRequestId] = useState<string | null>(
    null,
  );
  const [requestDetail, setRequestDetail] =
    useState<ServiceRequestSummary | null>(null);
  const [offers, setOffers] = useState<readonly OfferSummary[]>([]);
  const [offerSort, setOfferSort] = useState<
    "recommended" | "price" | "time" | "rating"
  >("recommended");
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  const [inbox, setInbox] = useState<readonly BusinessInboxItem[]>([]);
  const [businessMessages, setBusinessMessages] = useState<
    readonly import("@ghalishor/contracts").BusinessDirectMessageSummary[]
  >([]);
  const [businessUnreadMessages, setBusinessUnreadMessages] = useState(0);
  const [flowType, setFlowType] = useState<MatchingFlowType>("auto_broadcast");
  const [carpetType, setCarpetType] = useState<CarpetType>("handmade");
  const [carpetCount, setCarpetCount] = useState("1");
  const [approximateSqm, setApproximateSqm] = useState("1");
  const [selectedServices, setSelectedServices] = useState<ServiceCatalogKey[]>(
    ["washing_standard"],
  );
  const [requestItems, setRequestItems] = useState<ServiceRequestItem[]>([]);
  const [notes, setNotes] = useState("");
  const [maxReturnWaitDays, setMaxReturnWaitDays] = useState("5");
  const [matchingPreference, setMatchingPreference] =
    useState<CustomerMatchingPreference>("balanced");
  const [needPickup, setNeedPickup] = useState(true);
  const [needDelivery, setNeedDelivery] = useState(true);
  const [floorCount, setFloorCount] = useState("0");
  const [hasElevator, setHasElevator] = useState(false);

  useEffect(() => {
    const onPopState = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      setBusy(true);
      try {
        await api.refreshSession();
        await api.currentUser();
        try {
          const status = await api.passwordStatus();
          if (!cancelled) setMustChangePassword(status.mustChangePassword);
        } catch {
          if (!cancelled) setMustChangePassword(false);
        }
      } catch {
        sessionStore.clearAuthentication();
        if (!cancelled) setMustChangePassword(false);
      } finally {
        if (!cancelled) {
          setBusy(false);
          setRevision((value) => value + 1);
        }
      }
    };
    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const snapshot = useMemo(() => sessionStore.snapshot(), [revision]);
  const appSurface = useMemo(() => resolveAppSurface(), []);
  const requestedRoute = resolveIdentityWebRoute(pathname);
  const route = enforceIdentityRoute(
    requestedRoute,
    snapshot,
    identityFlow.getPendingOtp() !== null,
    loginIntent,
    appSurface,
    mustChangePassword,
  );

  useEffect(() => {
    if (!busy && route !== requestedRoute) navigate(route);
  }, [busy, requestedRoute, route]);

  useEffect(() => {
    const impersonation = snapshot.impersonation;
    if (impersonation === null) return;
    const banner = document.createElement("div");
    banner.className = "ghalishor-impersonation-banner";
    banner.setAttribute("role", "status");
    const message = document.createElement("strong");
    const target =
      impersonation.targetDisplayName?.trim() || impersonation.targetMobileE164;
    message.textContent = `در حال مشاهده و کار به‌جای ${target}`;
    const detail = document.createElement("span");
    detail.textContent =
      impersonation.intent === "business"
        ? "فضای کاربر قالیشویی"
        : "فضای مشتری";
    const stop = document.createElement("button");
    stop.type = "button";
    stop.textContent = "بازگشت فوری به مدیریت پلتفرم";
    stop.addEventListener("click", () => {
      stop.disabled = true;
      stop.textContent = "در حال بازگشت…";
      void api
        .stopImpersonation()
        .then(() => {
          writeLoginIntent(window.localStorage, "platform");
          setLoginIntent("platform");
          setError(null);
          setRevision((value) => value + 1);
          navigate(IDENTITY_WEB_ROUTES.platformUsers);
        })
        .catch((stopError: unknown) => {
          stop.disabled = false;
          stop.textContent = "تلاش دوباره برای بازگشت به مدیریت";
          setError(identityErrorMessage(stopError));
        });
    });
    banner.append(message, detail, stop);
    document.body.appendChild(banner);
    document.body.classList.add("has-ghalishor-impersonation");
    return () => {
      banner.remove();
      document.body.classList.remove("has-ghalishor-impersonation");
    };
  }, [api, snapshot.impersonation]);

  const run = async (operation: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await operation();
      setRevision((value) => value + 1);
    } catch (operationError) {
      setError(identityErrorMessage(operationError));
    } finally {
      setBusy(false);
    }
  };

  const applyCustomerPosition = async (
    position: GeolocationPosition,
    persist: boolean,
  ): Promise<ProfileResponse | null> => {
    const lat = position.coords.latitude;
    const lng = position.coords.longitude;
    let resolvedAddress: string | null = null;
    let resolvedCity: string | null = null;
    try {
      const geocoded = await api.reverseGeocode(lat, lng);
      resolvedAddress = geocoded.addressText;
      resolvedCity = geocoded.city;
    } catch {
      // Coordinates remain useful even when every address provider is unavailable.
    }
    setAddressLat(String(lat));
    setAddressLng(String(lng));
    if (resolvedAddress) {
      const details = parseAddressDetails(resolvedAddress);
      setAddressText(details.base || resolvedAddress);
      if (details.plaque) setPlaque(details.plaque);
      if (details.floorUnit) setFloorUnit(details.floorUnit);
    }
    if (resolvedCity) setCity(resolvedCity);
    setInfo(
      resolvedAddress
        ? "موقعیت و نشانی تقریبی پیدا شد؛ نشانی را بررسی و در صورت نیاز ویرایش کنید."
        : "موقعیت پیدا شد؛ لطفاً نشانی نوشتاری را تکمیل کنید.",
    );
    if (!persist) return null;
    const updated = await api.updateProfile({
      addressLat: lat,
      addressLng: lng,
      ...(resolvedAddress ? { addressText: resolvedAddress } : {}),
      ...(resolvedCity ? { city: resolvedCity } : {}),
    });
    setProfile(updated);
    return updated;
  };

  const enterAsUser = async (input: {
    targetUserId: string;
    intent: "customer" | "business";
    businessId?: string;
  }) => {
    const response = await api.startImpersonation(input);
    if (input.intent === "business" && input.businessId) {
      sessionStore.setSelectedBusinessId(input.businessId);
    } else {
      sessionStore.clearSelectedBusinessId();
    }
    writeLoginIntent(window.localStorage, input.intent);
    setLoginIntent(input.intent);
    setMustChangePassword(false);
    setRevision((value) => value + 1);
    navigate(
      routeAfterAuthentication(
        response.memberships,
        sessionStore.getSelectedBusinessId(),
        input.intent,
      ),
    );
  };

  const chooseIntent = (intent: LoginIntent) => {
    writeLoginIntent(window.localStorage, intent);
    setLoginIntent(intent);
    setError(null);
    setMobile("");
    setCode("");
    navigate(
      intent === "customer"
        ? IDENTITY_WEB_ROUTES.loginCustomer
        : intent === "business"
          ? IDENTITY_WEB_ROUTES.loginBusiness
          : IDENTITY_WEB_ROUTES.loginPlatform,
    );
  };

  const submitMobile = (event: FormEvent) => {
    event.preventDefault();
    const intent =
      loginIntent ??
      (route === IDENTITY_WEB_ROUTES.loginBusiness
        ? "business"
        : route === IDENTITY_WEB_ROUTES.loginPlatform
          ? "platform"
          : "customer");
    void run(async () => {
      writeLoginIntent(window.localStorage, intent);
      setLoginIntent(intent);
      webOtpListening.current?.controller.abort();
      webOtpListening.current = beginWebOtpListening();
      const response = await api.requestOtp(mobile);
      identityFlow.setPendingOtp(mobile, response, intent);
      if (response._devCode) setCode(response._devCode);
      navigate(IDENTITY_WEB_ROUTES.verify);
    });
  };

  const verifyOtpCode = (candidateCode: string) => {
    const normalizedCode = normalizeOtpDigits(candidateCode);
    void run(async () => {
      const pending = identityFlow.getPendingOtp();
      if (pending === null) throw new Error("OTP challenge is missing");
      if (!/^\d{6}$/.test(normalizedCode)) {
        throw new Error("کد شش‌رقمی پیامک‌شده را کامل وارد کنید.");
      }
      const response = await api.verifyOtp({
        challengeId: pending.challengeId,
        mobile: pending.mobile,
        code: normalizedCode,
      });
      writeLoginIntent(window.localStorage, pending.intent);
      setLoginIntent(pending.intent);
      identityFlow.clearPendingOtp();
      if (pending.intent === "customer") {
        const customerProfile = await api.getProfile();
        if (!isCustomerProfileComplete(customerProfile)) {
          setProfile(customerProfile);
          setProfileOnboarding(true);
          profileLocationAttempted.current = false;
          navigate(IDENTITY_WEB_ROUTES.customerProfile);
          return;
        }
      }
      navigate(
        routeAfterAuthentication(
          response.memberships,
          sessionStore.getSelectedBusinessId(),
          pending.intent,
        ),
      );
    });
  };

  const submitOtp = (event: FormEvent) => {
    event.preventDefault();
    verifyOtpCode(code);
  };

  useEffect(() => {
    if (route !== IDENTITY_WEB_ROUTES.verify) return;
    const listening = webOtpListening.current ?? beginWebOtpListening();
    if (!listening) return;
    webOtpListening.current = listening;
    let active = true;
    void listening.promise.then((receivedCode) => {
      if (!active || receivedCode === null) return;
      setCode(receivedCode);
      verifyOtpCode(receivedCode);
    });
    return () => {
      active = false;
      listening.controller.abort();
      if (webOtpListening.current === listening) webOtpListening.current = null;
    };
  }, [route]);

  const selectBusiness = (membership: IdentityMembershipSummary) => {
    sessionStore.setSelectedBusinessId(membership.businessId);
    setRevision((value) => value + 1);
    navigate(IDENTITY_WEB_ROUTES.businessHome);
  };

  const createBusiness = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      await api.createBusiness(businessName, newIdempotencyKey());
      setInfo("درخواست همکاری ثبت شد و پس از بررسی مدیران پلتفرم فعال می‌شود.");
      navigate(IDENTITY_WEB_ROUTES.businessSelect);
    });
  };

  const logoutDevice = () => {
    void run(async () => {
      await api.logout();
      clearLoginIntent(window.localStorage);
      setLoginIntent(null);
      setProfile(null);
      setProfileReturnRoute(null);
      setMustChangePassword(false);
      navigate(IDENTITY_WEB_ROUTES.home);
    });
  };

  const applyRequestForm = (request: ServiceRequestSummary | null) => {
    if (request === null) {
      setEditingRequestId(null);
      setCarpetType("handmade");
      setCarpetCount("1");
      setApproximateSqm("1");
      setSelectedServices(["washing_standard"]);
      setRequestItems([]);
      setNotes("");
      setMaxReturnWaitDays("5");
      setMatchingPreference("balanced");
      setNeedPickup(true);
      setNeedDelivery(true);
      setFloorCount("0");
      setHasElevator(false);
      setFlowType("auto_broadcast");
      return;
    }
    setEditingRequestId(request.id);
    setCarpetType(request.carpetType);
    setCarpetCount(String(request.carpetCount));
    setApproximateSqm(String(request.approximateSqm));
    setSelectedServices([...request.services]);
    setRequestItems([...request.items]);
    setNotes(request.notes ?? "");
    setMaxReturnWaitDays(
      request.maxReturnWaitDays == null
        ? "5"
        : String(request.maxReturnWaitDays),
    );
    setFlowType(request.flowType ?? "auto_broadcast");
    setMatchingPreference(request.matchingPreference ?? "balanced");
    setNeedPickup(request.needPickup !== false);
    setNeedDelivery(request.needDelivery !== false);
    setFloorCount(String(request.floorCount ?? 0));
    setHasElevator(request.hasElevator === true);
  };

  const toggleService = (key: ServiceCatalogKey) => {
    setSelectedServices((current) =>
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key],
    );
  };

  const requestPayload = () => ({
    items: requestItems,
    carpetType,
    carpetCount: Number(carpetCount),
    approximateSqm: Number(approximateSqm),
    services: selectedServices,
    notes: notes.trim().length === 0 ? null : notes.trim(),
    maxReturnWaitDays: Number(maxReturnWaitDays) || 5,
    matchingPreference,
    needPickup,
    needDelivery,
    floorCount: Number(floorCount) || 0,
    hasElevator,
  });

  useEffect(() => {
    const businessId = sessionStore.getSelectedBusinessId();
    if (
      (route !== IDENTITY_WEB_ROUTES.businessHome &&
        route !== IDENTITY_WEB_ROUTES.businessInbox &&
        route !== IDENTITY_WEB_ROUTES.businessActive &&
        route !== IDENTITY_WEB_ROUTES.businessMessages &&
        route !== IDENTITY_WEB_ROUTES.businessProfile &&
        route !== IDENTITY_WEB_ROUTES.businessConsole &&
        route !== IDENTITY_WEB_ROUTES.businessConsoleRates &&
        route !== IDENTITY_WEB_ROUTES.businessConsoleLogistics &&
        route !== IDENTITY_WEB_ROUTES.businessConsoleReports &&
        route !== IDENTITY_WEB_ROUTES.businessConsoleTeam &&
        route !== IDENTITY_WEB_ROUTES.businessConsoleFleet &&
        route !== IDENTITY_WEB_ROUTES.businessConsoleProfile &&
        route !== IDENTITY_WEB_ROUTES.businessConsoleSettings) ||
      businessId === null
    ) {
      if (
        route !== IDENTITY_WEB_ROUTES.businessHome &&
        route !== IDENTITY_WEB_ROUTES.businessActive &&
        route !== IDENTITY_WEB_ROUTES.businessMessages &&
        route !== IDENTITY_WEB_ROUTES.businessProfile &&
        route !== IDENTITY_WEB_ROUTES.businessConsoleProfile
      ) {
        setContext(null);
      }
      return;
    }

    let cancelled = false;
    setBusy(true);
    setError(null);
    void api
      .businessContext(businessId)
      .then(async (loaded) => {
        if (!cancelled) setContext(loaded);
      })
      .catch((contextError: unknown) => {
        if (!cancelled) setError(identityErrorMessage(contextError));
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [route, snapshot.selectedBusinessId]);

  useEffect(() => {
    const businessId = sessionStore.getSelectedBusinessId();
    if (
      route !== IDENTITY_WEB_ROUTES.businessInbox ||
      businessId === null ||
      !snapshot.authenticated
    ) {
      return;
    }
    let cancelled = false;
    setBusy(true);
    setError(null);
    void Promise.all([
      api.businessInbox(businessId),
      api.listBusinessDirectMessages(businessId),
    ])
      .then(([loaded, messages]) => {
        if (!cancelled) {
          setInbox(loaded);
          setBusinessMessages(messages.items);
        }
      })
      .catch((inboxError: unknown) => {
        if (!cancelled) setError(identityErrorMessage(inboxError));
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [route, snapshot.selectedBusinessId, snapshot.authenticated, revision]);

  useEffect(() => {
    if (
      route !== IDENTITY_WEB_ROUTES.customerRequestDetail ||
      !snapshot.authenticated ||
      viewingRequestId === null
    ) {
      return;
    }
    let cancelled = false;
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        const detail = await api.getRequest(viewingRequestId);
        if (cancelled) return;
        setRequestDetail(detail);
        if (
          detail.status === "offered" ||
          detail.status === "waiting_acceptance" ||
          detail.status === "matching"
        ) {
          const loadedOffers = await api.listRequestOffers(viewingRequestId);
          if (!cancelled) setOffers(loadedOffers);
        } else {
          setOffers([]);
        }
      } catch (detailError: unknown) {
        if (!cancelled) setError(identityErrorMessage(detailError));
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [route, snapshot.authenticated, viewingRequestId, revision]);

  useEffect(() => {
    if (
      route !== IDENTITY_WEB_ROUTES.businessHome ||
      !snapshot.authenticated ||
      sessionStore.getSelectedBusinessId() === null
    ) {
      return;
    }
    let cancelled = false;
    const refreshUnread = () => {
      void api
        .listConversations()
        .then((response) => {
          if (!cancelled) setBusinessUnreadMessages(response.unreadCount);
        })
        .catch(() => undefined);
    };
    refreshUnread();
    const timer = window.setInterval(refreshUnread, 10000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [api, route, snapshot.authenticated, snapshot.selectedBusinessId]);

  useEffect(() => {
    if (
      route !== IDENTITY_WEB_ROUTES.customerProfile ||
      !snapshot.authenticated
    )
      return;
    let cancelled = false;
    setBusy(true);
    setError(null);
    void api
      .getProfile()
      .then(async (loaded) => {
        if (cancelled) return;
        setProfile(loaded);
        setFullName(loaded.fullName ?? "");
        setCity(loaded.city ?? "");
        const details = parseAddressDetails(loaded.addressText ?? "");
        setAddressText(details.base || loaded.addressText || "");
        setPlaque(details.plaque);
        setFloorUnit(details.floorUnit);
        setAddressLat(
          loaded.addressLat == null ? "" : String(loaded.addressLat),
        );
        setAddressLng(
          loaded.addressLng == null ? "" : String(loaded.addressLng),
        );
        if (
          profileOnboarding &&
          loaded.addressLat === null &&
          loaded.addressLng === null &&
          !profileLocationAttempted.current &&
          navigator.geolocation
        ) {
          profileLocationAttempted.current = true;
          try {
            const position = await new Promise<GeolocationPosition>(
              (resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                  enableHighAccuracy: true,
                  timeout: 20_000,
                  maximumAge: 60_000,
                });
              },
            );
            if (!cancelled) await applyCustomerPosition(position, false);
          } catch {
            if (!cancelled)
              setInfo(
                "برای تکمیل نشانی، دکمه دریافت موقعیت را بزنید و اجازه دسترسی را تأیید کنید.",
              );
          }
        }
      })
      .catch((profileError: unknown) => {
        if (!cancelled) setError(identityErrorMessage(profileError));
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [route, snapshot.authenticated, profileOnboarding]);

  useEffect(() => {
    if (
      route !== IDENTITY_WEB_ROUTES.customerRequests ||
      !snapshot.authenticated
    )
      return;
    let cancelled = false;
    setBusy(true);
    setError(null);
    void api
      .listRequests()
      .then((loaded) => {
        if (!cancelled) setRequests(loaded);
      })
      .catch((listError: unknown) => {
        if (!cancelled) setError(identityErrorMessage(listError));
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [route, snapshot.authenticated, revision]);

  useEffect(() => {
    if (route === IDENTITY_WEB_ROUTES.customerRequestNew) {
      applyRequestForm(null);
      return;
    }
    if (
      route !== IDENTITY_WEB_ROUTES.customerRequestEdit ||
      editingRequestId === null
    )
      return;
    let cancelled = false;
    setBusy(true);
    setError(null);
    void api
      .getRequest(editingRequestId)
      .then((loaded) => {
        if (!cancelled) applyRequestForm(loaded);
      })
      .catch((loadError: unknown) => {
        if (!cancelled) {
          setError(identityErrorMessage(loadError));
          navigate(IDENTITY_WEB_ROUTES.customerRequests);
        }
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [route, editingRequestId]);

  useEffect(() => {
    if (route !== IDENTITY_WEB_ROUTES.businessActive || !snapshot.authenticated)
      return;
    const businessId = sessionStore.getSelectedBusinessId();
    if (!businessId) return;
    let cancelled = false;
    setBusy(true);
    setError(null);
    void api
      .businessActive(businessId)
      .then((loaded) => {
        if (!cancelled) setActiveJobs(loaded);
      })
      .catch((loadError: unknown) => {
        if (!cancelled) setError(identityErrorMessage(loadError));
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [route, snapshot.authenticated, revision]);

  useEffect(() => {
    if (
      (route !== IDENTITY_WEB_ROUTES.businessProfile &&
        route !== IDENTITY_WEB_ROUTES.businessConsoleProfile) ||
      !snapshot.authenticated
    )
      return;
    const businessId = sessionStore.getSelectedBusinessId();
    if (!businessId) return;
    let cancelled = false;
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        const profile = await api.getBusinessProfile(businessId);
        const rates = await api.listRateCards(businessId);
        if (cancelled) return;
        setBizCity(profile.city ?? "");
        setBizAddress(profile.addressText ?? "");
        setBizLat(profile.lat == null ? "" : String(profile.lat));
        setBizLng(profile.lng == null ? "" : String(profile.lng));
        setBizRadius(String(profile.serviceRadiusKm ?? 15));
        setBizTypicalReturnDays(String(profile.typicalReturnDays ?? 5));
        const washing = rates.find(
          (r) => r.serviceKey === "washing" && r.carpetType === "handmade",
        );
        if (washing) setRateWashing(String(washing.pricePerSqm));
      } catch (loadError: unknown) {
        if (!cancelled) setError(identityErrorMessage(loadError));
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [route, snapshot.authenticated, revision]);

  if (
    busy &&
    snapshot.authenticated === false &&
    requestedRoute !== IDENTITY_WEB_ROUTES.verify &&
    requestedRoute !== IDENTITY_WEB_ROUTES.partners &&
    requestedRoute !== IDENTITY_WEB_ROUTES.downloads
  ) {
    return (
      <Shell
        title="در حال بررسی نشست"
        description="ارتباط امن با سرور در حال بررسی است."
      >
        <div className="session-check-status" role="status" aria-live="polite">
          <span className="session-check-spinner" aria-hidden="true" />
          <strong>لطفاً چند لحظه صبر کنید…</strong>
        </div>
      </Shell>
    );
  }

  if (route === IDENTITY_WEB_ROUTES.partners) {
    return <PartnersPage api={api} Shell={Shell} onNavigate={navigate} />;
  }

  if (route === IDENTITY_WEB_ROUTES.downloads) {
    return <DownloadsPage onBack={() => navigate(IDENTITY_WEB_ROUTES.home)} />;
  }

  if (
    isPlatformWebRoute(route) ||
    route === IDENTITY_WEB_ROUTES.loginPlatform
  ) {
    if (
      !snapshot.authenticated &&
      route === IDENTITY_WEB_ROUTES.loginPlatform
    ) {
      // fall through to platform login form below via loginPlatform branch
    } else if (!snapshot.authenticated) {
      return (
        <Shell
          title="فضای پلتفرم"
          description="برای دسترسی باید وارد حساب شوید."
        >
          <StatusMessage kind="error">ورود لازم است.</StatusMessage>
          <button
            type="button"
            onClick={() => navigate(IDENTITY_WEB_ROUTES.loginPlatform)}
          >
            ورود
          </button>
        </Shell>
      );
    } else if (isPlatformWebRoute(route)) {
      return (
        <Suspense
          fallback={
            <div className="identity-page" dir="rtl">
              در حال آماده‌سازی پنل مدیریت…
            </div>
          }
        >
          <PlatformConsole
            api={api}
            route={route}
            requestId={platformRequestId}
            onNavigate={navigate}
            onOpenRequest={setPlatformRequestId}
            onImpersonate={enterAsUser}
            onLogout={() => {
              void run(async () => {
                await api.logout();
                clearLoginIntent(window.localStorage);
                setLoginIntent(null);
                setProfile(null);
                setProfileReturnRoute(null);
                navigate(IDENTITY_WEB_ROUTES.loginPlatform);
              });
            }}
          />
        </Suspense>
      );
    }
  }

  if (
    appSurface === "platform" &&
    (route === IDENTITY_WEB_ROUTES.home || route === IDENTITY_WEB_ROUTES.login)
  ) {
    navigate(IDENTITY_WEB_ROUTES.loginPlatform);
    return null;
  }

  if (
    route === IDENTITY_WEB_ROUTES.home ||
    route === IDENTITY_WEB_ROUTES.login
  ) {
    return (
      <Shell
        title={<BrandWelcome />}
        description="سفارش خود را ساده ثبت کنید؛ قالیشور از شروع تا تحویل همراه شماست."
        voiceExpert={voiceExpert}
        voiceScreen="welcome"
        voiceAuthenticated={false}
        voiceGuideAutoSpeak={false}
        voicePermissionPrompt
        voiceAfterChildren
        entry
      >
        <button
          type="button"
          className="welcome-expert__customer-entry"
          onClick={() => chooseIntent("customer")}
        >
          <span aria-hidden="true">←</span>
          ورود و ثبت سفارش
        </button>
        <button
          type="button"
          className="entry-secondary-action entry-business-visible"
          onClick={() => chooseIntent("business")}
        >
          <b
            className="entry-secondary-mark entry-secondary-mark-business"
            aria-hidden="true"
          />
          <span>
            <strong>ورود قالیشویی</strong>
            <small>فضای قالیشویی یا درخواست همکاری</small>
          </span>
        </button>
        <CustomerEntryHighlights />
        <details className="entry-more-sections">
          <summary>سایر بخش‌ها</summary>
          <div className="account-actions account-actions-entry">
            <button
              type="button"
              className="entry-secondary-action"
              onClick={() => navigate(IDENTITY_WEB_ROUTES.downloads)}
            >
              <b
                className="entry-secondary-mark entry-secondary-mark-download"
                aria-hidden="true"
              />
              <span>
                <strong>دانلود اپ‌ها و نصب روی آیفون</strong>
                <small>نسخه‌های مشتری و قالیشویی</small>
              </span>
            </button>
            <button
              type="button"
              className="entry-secondary-action"
              onClick={() => navigate(IDENTITY_WEB_ROUTES.partners)}
            >
              <b
                className="entry-secondary-mark entry-secondary-mark-partners"
                aria-hidden="true"
              />
              <span>
                <strong>شرکت‌ها و سازمان‌ها</strong>
                <small>شروع همکاری سازمانی</small>
              </span>
            </button>
          </div>
        </details>
        {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
      </Shell>
    );
  }

  if (route === IDENTITY_WEB_ROUTES.resetPassword) {
    const resetToken =
      new URLSearchParams(window.location.search).get("token") ?? "";
    return (
      <Shell
        title={<BrandWelcome />}
        description="رمز تازه‌ای برای حساب مشتری تعریف کنید."
        eyebrow="بازیابی امن حساب"
        showToday
      >
        <form
          className="identity-form"
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              if (!resetToken) throw new Error("پیوند بازیابی کامل نیست.");
              if (newPassword !== confirmPassword)
                throw new Error("تکرار رمز عبور با رمز جدید یکسان نیست.");
              await api.resetPassword(resetToken, newPassword);
              sessionStore.clearAuthentication();
              setCustomerAuthMode("login");
              setPassword("");
              setNewPassword("");
              setConfirmPassword("");
              setInfo("رمز عبور با موفقیت تغییر کرد؛ اکنون وارد شوید.");
              navigate(IDENTITY_WEB_ROUTES.loginCustomer);
            });
          }}
        >
          <PasswordField
            label="رمز عبور جدید"
            value={newPassword}
            visible={showPassword}
            onChange={setNewPassword}
            onToggle={() => setShowPassword((value) => !value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
          <small className="field-hint">
            حداقل ۸ نویسه و شامل حداقل یک حرف و یک عدد
          </small>
          <PasswordField
            label="تکرار رمز جدید"
            value={confirmPassword}
            visible={showConfirmPassword}
            onChange={setConfirmPassword}
            onToggle={() => setShowConfirmPassword((value) => !value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
          {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
          <button type="submit" disabled={busy || !resetToken}>
            {busy ? "در حال ذخیره…" : "ذخیره رمز جدید"}
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => navigate(IDENTITY_WEB_ROUTES.loginCustomer)}
          >
            بازگشت به ورود
          </button>
        </form>
      </Shell>
    );
  }

  if (
    route === IDENTITY_WEB_ROUTES.loginCustomer ||
    route === IDENTITY_WEB_ROUTES.loginBusiness ||
    route === IDENTITY_WEB_ROUTES.loginPlatform
  ) {
    const isCustomer = route === IDENTITY_WEB_ROUTES.loginCustomer;
    const isPlatform = route === IDENTITY_WEB_ROUTES.loginPlatform;
    if (isCustomer) {
      return (
        <Shell
          title={<BrandWelcome />}
          description="شماره موبایل خود را وارد کنید؛ کد ورود برایتان پیامک می‌شود."
          eyebrow="ورود مشتری"
          showToday
          voiceExpert={voiceExpert}
          voiceScreen="login"
          voiceAuthenticated={false}
          entry
        >
          <form className="identity-form" onSubmit={submitMobile}>
            <div className="auth-form-heading">
              <h2>ورود با شماره موبایل</h2>
              <p>بدون رمز عبور و ثبت‌نام جداگانه</p>
            </div>
            <label>
              شماره موبایل
              <input
                inputMode="tel"
                autoComplete="tel"
                value={mobile}
                onChange={(event) => setMobile(event.target.value)}
                placeholder="09121234567"
                dir="ltr"
                required
              />
            </label>
            {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
            <button type="submit" disabled={busy}>
              {busy ? "در حال ارسال…" : "دریافت کد ورود"}
            </button>
            <small className="field-hint auth-otp-hint">
              پس از تأیید کد، کارشناس گویای قالیشور مرحله‌به‌مرحله همراهتان است.
            </small>
            <button
              type="button"
              className="secondary auth-back"
              onClick={() => navigate(IDENTITY_WEB_ROUTES.home)}
            >
              بازگشت به صفحه اصلی
            </button>
          </form>
        </Shell>
      );
    }
    if (isCustomer) {
      return (
        <Shell
          title={<BrandWelcome />}
          description="برای شروع حساب مشتری بسازید؛ اگر قبلاً ثبت‌نام کرده‌اید با شماره موبایل و رمز عبور وارد شوید."
          eyebrow="حساب مشتری"
          showToday
        >
          {customerAuthMode === "register" ? (
            <form
              className="identity-form"
              onSubmit={(event) => {
                event.preventDefault();
                void run(async () => {
                  if (password !== confirmPassword)
                    throw new Error(
                      "تکرار رمز عبور با رمز انتخابی یکسان نیست.",
                    );
                  writeLoginIntent(window.localStorage, "customer");
                  setLoginIntent("customer");
                  await api.passwordRegister({
                    mobile,
                    email,
                    displayName: fullName,
                    password,
                  });
                  navigate(IDENTITY_WEB_ROUTES.customerHome);
                });
              }}
            >
              <div className="auth-form-heading">
                <h2>ساخت حساب کاربری</h2>
                <p>
                  اطلاعاتتان را یک‌بار وارد کنید تا حساب مشتری شما ساخته شود.
                </p>
              </div>
              <label>
                نام و نام خانوادگی
                <input
                  autoComplete="name"
                  value={fullName}
                  onChange={(event) => setFullName(event.target.value)}
                  required
                  minLength={2}
                />
              </label>
              <label>
                شماره موبایل
                <input
                  inputMode="tel"
                  autoComplete="tel"
                  value={mobile}
                  onChange={(event) => setMobile(event.target.value)}
                  placeholder="09121234567"
                  required
                />
              </label>
              <label>
                ایمیل
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="name@example.com"
                  dir="ltr"
                  required
                />
              </label>
              <PasswordField
                label="رمز عبور"
                value={password}
                visible={showPassword}
                onChange={setPassword}
                onToggle={() => setShowPassword((value) => !value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
              <small className="field-hint">
                حداقل ۸ نویسه و شامل حداقل یک حرف و یک عدد
              </small>
              <PasswordField
                label="تکرار رمز عبور"
                value={confirmPassword}
                visible={showConfirmPassword}
                onChange={setConfirmPassword}
                onToggle={() => setShowConfirmPassword((value) => !value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
              {error ? (
                <StatusMessage kind="error">{error}</StatusMessage>
              ) : null}
              <button type="submit" disabled={busy}>
                {busy ? "در حال ساخت حساب…" : "ساخت حساب و ورود"}
              </button>
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setCustomerAuthMode("login");
                  setError(null);
                  setInfo(null);
                }}
              >
                قبلاً حساب ساخته‌اید؟ ورود
              </button>
            </form>
          ) : customerAuthMode === "login" ? (
            <form
              className="identity-form"
              onSubmit={(event) => {
                event.preventDefault();
                void run(async () => {
                  writeLoginIntent(window.localStorage, "customer");
                  setLoginIntent("customer");
                  const response = await api.passwordLogin(mobile, password);
                  setMustChangePassword(response.mustChangePassword);
                  navigate(
                    response.mustChangePassword
                      ? IDENTITY_WEB_ROUTES.passwordChange
                      : IDENTITY_WEB_ROUTES.customerHome,
                  );
                });
              }}
            >
              <label>
                شماره موبایل
                <input
                  inputMode="tel"
                  autoComplete="tel"
                  value={mobile}
                  onChange={(event) => setMobile(event.target.value)}
                  placeholder="09121234567"
                  required
                />
              </label>
              <PasswordField
                label="رمز عبور"
                value={password}
                visible={showPassword}
                onChange={setPassword}
                onToggle={() => setShowPassword((value) => !value)}
                autoComplete="current-password"
                minLength={8}
                required
              />
              {info ? <StatusMessage kind="info">{info}</StatusMessage> : null}
              {error ? (
                <StatusMessage kind="error">{error}</StatusMessage>
              ) : null}
              <button type="submit" disabled={busy}>
                {busy ? "در حال ورود…" : "ورود به حساب"}
              </button>
              <button
                type="button"
                className="secondary auth-create-account"
                onClick={() => {
                  setCustomerAuthMode("register");
                  setError(null);
                  setInfo(null);
                }}
              >
                حساب ندارید؟ ساخت حساب کاربری
              </button>
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setCustomerAuthMode("forgot");
                  setError(null);
                  setInfo(null);
                }}
              >
                رمز عبورم را فراموش کرده‌ام
              </button>
            </form>
          ) : (
            <form
              className="identity-form"
              onSubmit={(event) => {
                event.preventDefault();
                void run(async () => {
                  const response = await api.forgotPassword(email);
                  setInfo(response.message);
                });
              }}
            >
              <h2 className="form-title">بازیابی رمز عبور</h2>
              <p className="form-note">
                ایمیلی را وارد کنید که هنگام ثبت‌نام نوشته‌اید.
              </p>
              <label>
                ایمیل
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="name@example.com"
                  dir="ltr"
                  required
                />
              </label>
              {info ? <StatusMessage kind="info">{info}</StatusMessage> : null}
              {error ? (
                <StatusMessage kind="error">{error}</StatusMessage>
              ) : null}
              <button type="submit" disabled={busy}>
                {busy ? "در حال ارسال…" : "ارسال پیوند بازیابی"}
              </button>
              <button
                type="button"
                className="secondary"
                onClick={() => {
                  setCustomerAuthMode("login");
                  setError(null);
                  setInfo(null);
                }}
              >
                بازگشت به ورود
              </button>
            </form>
          )}
          <button
            type="button"
            className="secondary auth-back"
            onClick={() => navigate(IDENTITY_WEB_ROUTES.home)}
          >
            بازگشت به انتخاب مسیر
          </button>
        </Shell>
      );
    }
    if (isPlatform) {
      return (
        <Shell
          title="ورود مدیر پلتفرم"
          description="با شماره موبایل مدیر و رمز عبور وارد کنسول مدیریت شوید."
          eyebrow="مدیریت پلتفرم"
          showToday
        >
          <form
            className="identity-form"
            onSubmit={(event) => {
              event.preventDefault();
              void run(async () => {
                writeLoginIntent(window.localStorage, "platform");
                setLoginIntent("platform");
                const response = await api.passwordLogin(mobile, password);
                sessionStore.setAccessGrant(response);
                setMustChangePassword(response.mustChangePassword);
                navigate(
                  response.mustChangePassword
                    ? IDENTITY_WEB_ROUTES.passwordChange
                    : IDENTITY_WEB_ROUTES.platformHome,
                );
              });
            }}
          >
            <label>
              شماره موبایل مدیر
              <input
                name="mobile"
                inputMode="tel"
                autoComplete="tel"
                value={mobile}
                onChange={(event) => setMobile(event.target.value)}
                placeholder="09121234567"
                required
              />
            </label>
            <PasswordField
              label="رمز عبور"
              value={password}
              visible={showPassword}
              onChange={setPassword}
              onToggle={() => setShowPassword((value) => !value)}
              autoComplete="current-password"
              minLength={8}
              required
            />
            {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
            <button
              type="submit"
              disabled={busy || mobile.trim().length < 8 || password.length < 8}
            >
              {busy ? "در حال ورود…" : "ورود به پنل مدیریت"}
            </button>
            <button
              type="button"
              className="secondary auth-back"
              onClick={() => navigate(IDENTITY_WEB_ROUTES.home)}
            >
              بازگشت به صفحه اصلی
            </button>
          </form>
        </Shell>
      );
    }
    return (
      <Shell
        title="ورود قالیشویی"
        description="ابتدا با شماره موبایل و رمز عبور وارد شوید؛ قالیشویی‌های جدید از همین صفحه درخواست همکاری ثبت می‌کنند."
        eyebrow="فضای قالیشویی"
        showToday
      >
        {businessAuthMode === "register" ? (
          <form
            className="identity-form"
            onSubmit={(event) => {
              event.preventDefault();
              void run(async () => {
                if (password !== confirmPassword)
                  throw new Error("تکرار رمز عبور با رمز انتخابی یکسان نیست.");
                writeLoginIntent(window.localStorage, "business");
                setLoginIntent("business");
                await api.passwordRegister({
                  mobile,
                  email,
                  displayName: fullName,
                  password,
                });
                await api.createBusiness(businessName, newIdempotencyKey());
                setInfo(
                  "درخواست همکاری ثبت شد و پس از تأیید مدیر پلتفرم فعال می‌شود.",
                );
                navigate(IDENTITY_WEB_ROUTES.businessSelect);
              });
            }}
          >
            <div className="auth-form-heading">
              <h2>درخواست همکاری قالیشویی</h2>
              <p>
                حساب شما ساخته می‌شود و فعال‌سازی کسب‌وکار پس از تأیید مدیر
                پلتفرم انجام خواهد شد.
              </p>
            </div>
            <label>
              نام و نام خانوادگی مسئول
              <input
                autoComplete="name"
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                required
                minLength={2}
              />
            </label>
            <label>
              نام قالیشویی
              <input
                value={businessName}
                onChange={(event) => setBusinessName(event.target.value)}
                required
                minLength={2}
              />
            </label>
            <label>
              شماره موبایل
              <input
                inputMode="tel"
                autoComplete="tel"
                value={mobile}
                onChange={(event) => setMobile(event.target.value)}
                placeholder="09121234567"
                required
              />
            </label>
            <label>
              ایمیل
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@example.com"
                dir="ltr"
                required
              />
            </label>
            <PasswordField
              label="رمز عبور"
              value={password}
              visible={showPassword}
              onChange={setPassword}
              onToggle={() => setShowPassword((value) => !value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
            <small className="field-hint">
              حداقل ۸ نویسه و شامل حداقل یک حرف و یک عدد
            </small>
            <PasswordField
              label="تکرار رمز عبور"
              value={confirmPassword}
              visible={showConfirmPassword}
              onChange={setConfirmPassword}
              onToggle={() => setShowConfirmPassword((value) => !value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
            {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
            <button type="submit" disabled={busy}>
              {busy ? "در حال ثبت…" : "ثبت و ارسال برای تأیید مدیر"}
            </button>
            <button
              type="button"
              className="link-button"
              onClick={() => {
                setBusinessAuthMode("login");
                setError(null);
                setInfo(null);
              }}
            >
              قبلاً حساب ساخته‌اید؟ ورود
            </button>
          </form>
        ) : businessAuthMode === "forgot" ? (
          <form
            className="identity-form"
            onSubmit={(event) => {
              event.preventDefault();
              void run(async () => {
                const response = await api.forgotPassword(email);
                setInfo(response.message);
              });
            }}
          >
            <h2 className="form-title">بازیابی رمز عبور</h2>
            <label>
              ایمیل
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@example.com"
                dir="ltr"
                required
              />
            </label>
            {info ? <StatusMessage kind="info">{info}</StatusMessage> : null}
            {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
            <button type="submit" disabled={busy}>
              {busy ? "در حال ارسال…" : "ارسال پیوند بازیابی"}
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() => {
                setBusinessAuthMode("login");
                setError(null);
                setInfo(null);
              }}
            >
              بازگشت به ورود
            </button>
          </form>
        ) : (
          <form
            className="identity-form"
            onSubmit={(event) => {
              event.preventDefault();
              void run(async () => {
                writeLoginIntent(window.localStorage, "business");
                setLoginIntent("business");
                const response = await api.passwordLogin(mobile, password);
                setMustChangePassword(response.mustChangePassword);
                navigate(
                  response.mustChangePassword
                    ? IDENTITY_WEB_ROUTES.passwordChange
                    : routeAfterAuthentication(
                        response.memberships,
                        sessionStore.getSelectedBusinessId(),
                        "business",
                      ),
                );
              });
            }}
          >
            <label>
              شماره موبایل
              <input
                inputMode="tel"
                autoComplete="tel"
                value={mobile}
                onChange={(event) => setMobile(event.target.value)}
                placeholder="09121234567"
                required
              />
            </label>
            <PasswordField
              label="رمز عبور"
              value={password}
              visible={showPassword}
              onChange={setPassword}
              onToggle={() => setShowPassword((value) => !value)}
              autoComplete="current-password"
              minLength={8}
              required
            />
            {info ? <StatusMessage kind="info">{info}</StatusMessage> : null}
            {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
            <button type="submit" disabled={busy}>
              {busy ? "در حال ورود…" : "ورود"}
            </button>
            <button
              type="button"
              className="secondary auth-create-account"
              onClick={() => {
                setBusinessAuthMode("register");
                setError(null);
                setInfo(null);
              }}
            >
              حساب ندارید؟ ثبت درخواست همکاری
            </button>
            <button
              type="button"
              className="link-button"
              onClick={() => {
                setBusinessAuthMode("forgot");
                setError(null);
                setInfo(null);
              }}
            >
              رمز عبورم را فراموش کرده‌ام
            </button>
          </form>
        )}
        <button
          type="button"
          className="secondary auth-back"
          onClick={() => navigate(IDENTITY_WEB_ROUTES.home)}
        >
          بازگشت به صفحه اصلی
        </button>
      </Shell>
    );
  }

  if (route === IDENTITY_WEB_ROUTES.verify) {
    const pending = identityFlow.getPendingOtp();
    const intent = pending?.intent ?? loginIntent ?? "customer";
    const intentLabel =
      intent === "platform"
        ? "پلتفرم"
        : intent === "business"
          ? "قالیشویی"
          : "مشتری";
    const loginBack =
      intent === "platform"
        ? IDENTITY_WEB_ROUTES.loginPlatform
        : intent === "business"
          ? IDENTITY_WEB_ROUTES.loginBusiness
          : IDENTITY_WEB_ROUTES.loginCustomer;
    const devMode = pending?.devCode !== null && pending?.devCode !== undefined;
    return (
      <Shell
        title="تأیید کد ورود"
        description={`کد شش‌رقمی برای ${pending?.mobile ?? "شماره شما"} در مسیر ${intentLabel} را وارد کنید.`}
        eyebrow={
          intent === "platform"
            ? "مسیر پلتفرم"
            : intent === "customer"
              ? "مسیر مشتری"
              : "مسیر قالیشویی"
        }
        voiceExpert={intent === "customer" ? voiceExpert : undefined}
        voiceScreen={intent === "customer" ? "otp" : undefined}
        voiceAuthenticated={false}
      >
        {devMode ? (
          <div className="dev-banner">
            حالت آزمایشی — کد به‌صورت خودکار پر می‌شود
          </div>
        ) : null}
        <form className="identity-form" onSubmit={submitOtp}>
          <label>
            کد تأیید
            <input
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(event) => {
                const nextCode = normalizeOtpDigits(event.target.value);
                setCode(nextCode);
                if (/^\d{6}$/.test(nextCode) && !busy) {
                  window.setTimeout(() => verifyOtpCode(nextCode), 0);
                }
              }}
              placeholder="123456"
              required
            />
          </label>
          {pending ? (
            <StatusMessage kind="info">
              اعتبار این کد تا {formatJalaliDateTime(pending.expiresAt)} است.
            </StatusMessage>
          ) : null}
          {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
          <button type="submit" disabled={busy || pending === null}>
            {busy ? "در حال بررسی…" : "ورود امن"}
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => {
              identityFlow.clearPendingOtp();
              navigate(loginBack);
            }}
          >
            تغییر شماره موبایل
          </button>
        </form>
      </Shell>
    );
  }

  if (route === IDENTITY_WEB_ROUTES.customerHome) {
    return (
      <Shell
        title="فضای مشتری"
        description=""
        immersive
        onAccount={() => navigate(IDENTITY_WEB_ROUTES.account)}
        voiceExpert={voiceExpert}
        voiceScreen="home"
      >
        {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
        <CustomerHomeHero
          mobile={snapshot.user?.mobileE164}
          onNewRequest={() => {
            applyRequestForm(null);
            navigate(IDENTITY_WEB_ROUTES.customerRequestNew);
          }}
          onMyRequests={() => navigate(IDENTITY_WEB_ROUTES.customerRequests)}
          onMessages={() => navigate(IDENTITY_WEB_ROUTES.customerMessages)}
          onDownloadApps={() => navigate(IDENTITY_WEB_ROUTES.downloads)}
        />
      </Shell>
    );
  }

  if (route === IDENTITY_WEB_ROUTES.customerMessages) {
    return (
      <Shell
        title="پیام‌ها و پشتیبانی"
        description="ارتباط زنده با پشتیبانی پلتفرم و قالیشویی مرتبط با سفارش شما."
        onAccount={() => navigate(IDENTITY_WEB_ROUTES.account)}
        eyebrow="مسیر مشتری"
        voiceExpert={voiceExpert}
        voiceScreen="messages"
      >
        <MessagingCenter api={api} mode="customer" />
      </Shell>
    );
  }

  if (route === IDENTITY_WEB_ROUTES.customerProfile) {
    return (
      <Shell
        title={profileOnboarding ? "تکمیل مشخصات" : "پروفایل مشتری"}
        description="کارشناس گویای قالیشور قدم‌به‌قدم کمک می‌کند تا اطلاعات لازم برای دریافت خدمت کامل شود."
        onAccount={() => navigate(IDENTITY_WEB_ROUTES.account)}
        eyebrow="مسیر مشتری"
        voiceExpert={voiceExpert}
        voiceScreen="profile"
        onVoiceGuidePlay={() => {
          if (fullName.trim().length < 2) profileNameInput.current?.focus();
          else if (city.trim() === "") profileCityInput.current?.focus();
          else if (addressText.trim() === "")
            profileAddressInput.current?.focus();
        }}
      >
        <form
          className="identity-form customer-profile-form"
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              if (
                fullName.trim().length < 2 ||
                city.trim() === "" ||
                addressText.trim() === "" ||
                addressLat.trim() === "" ||
                addressLng.trim() === ""
              ) {
                throw new Error(
                  "برای دریافت خدمت، نام، شهر، آدرس و موقعیت را کامل کنید.",
                );
              }
              const saved = await api.updateProfile({
                fullName: fullName.trim() || null,
                city: city.trim() || null,
                addressText:
                  composeAddressWithDetails({
                    base: addressText,
                    plaque,
                    floorUnit,
                  }) || null,
                addressLat:
                  addressLat.trim() === "" ? null : Number(addressLat),
                addressLng:
                  addressLng.trim() === "" ? null : Number(addressLng),
              });
              setProfile(saved);
              setInfo("پروفایل ذخیره شد و آماده ثبت درخواست هستید.");
              if (profileOnboarding) {
                setProfileOnboarding(false);
                navigate(IDENTITY_WEB_ROUTES.customerHome);
                return;
              }
              if (profileReturnRoute) {
                const returnRoute = profileReturnRoute;
                setProfileReturnRoute(null);
                navigate(returnRoute);
              }
            });
          }}
        >
          <label>
            نام کامل
            <input
              ref={profileNameInput}
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              maxLength={200}
              required
            />
          </label>
          <label>
            شهر
            <input
              ref={profileCityInput}
              value={city}
              onChange={(event) => setCity(event.target.value)}
              maxLength={100}
            />
          </label>
          <label>
            آدرس
            <textarea
              ref={profileAddressInput}
              value={addressText}
              onChange={(event) => setAddressText(event.target.value)}
              rows={3}
              required
            />
          </label>
          <label>
            پلاک (اختیاری)
            <input
              value={plaque}
              onChange={(event) => setPlaque(event.target.value)}
              maxLength={40}
              placeholder="مثلاً ۱۲"
            />
          </label>
          <label>
            طبقه و واحد (اختیاری)
            <input
              value={floorUnit}
              onChange={(event) => setFloorUnit(event.target.value)}
              maxLength={80}
              placeholder="مثلاً طبقه ۳ واحد ۴"
            />
          </label>
          <button
            type="button"
            className="secondary"
            disabled={busy}
            onClick={() => {
              if (!navigator.geolocation) {
                setError("مرورگر موقعیت مکانی را پشتیبانی نمی‌کند.");
                return;
              }
              setBusy(true);
              setError(null);
              navigator.geolocation.getCurrentPosition(
                (pos) => {
                  void applyCustomerPosition(pos, false).finally(() =>
                    setBusy(false),
                  );
                },
                () => {
                  setError("اجازه موقعیت داده نشد یا موقعیت در دسترس نیست.");
                  setBusy(false);
                },
                { enableHighAccuracy: true, timeout: 20000 },
              );
            }}
          >
            دریافت خودکار موقعیت از دستگاه
          </button>
          <label>
            عرض جغرافیایی (خودکار)
            <input value={addressLat} readOnly placeholder="35.6892" />
          </label>
          <label>
            طول جغرافیایی (خودکار)
            <input value={addressLng} readOnly placeholder="51.3890" />
          </label>
          {profile ? (
            <StatusMessage kind="info">
              آخرین به‌روزرسانی: {formatJalaliDateTime(profile.updatedAt)}
            </StatusMessage>
          ) : null}
          {info ? <StatusMessage kind="info">{info}</StatusMessage> : null}
          {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
          <button type="submit" disabled={busy}>
            {busy
              ? "در حال ذخیره…"
              : profileOnboarding
                ? "ذخیره و دریافت خدمات"
                : "ذخیره پروفایل"}
          </button>
          {!profileOnboarding ? (
            <>
              <button
                type="button"
                className="secondary"
                onClick={() => navigate(IDENTITY_WEB_ROUTES.downloads)}
              >
                دانلود اپ اندروید
              </button>
              <button
                type="button"
                className="secondary"
                onClick={() => {
                  const returnRoute =
                    profileReturnRoute ?? IDENTITY_WEB_ROUTES.customerHome;
                  setProfileReturnRoute(null);
                  navigate(returnRoute);
                }}
              >
                {profileReturnRoute ? "بازگشت به درخواست" : "بازگشت"}
              </button>
            </>
          ) : null}
        </form>
      </Shell>
    );
  }

  if (route === IDENTITY_WEB_ROUTES.customerRequests) {
    return (
      <Shell
        title="درخواست‌های من"
        description="وضعیت هر درخواست را با تاریخ شمسی ببینید و ادامه دهید."
        onAccount={() => navigate(IDENTITY_WEB_ROUTES.account)}
        eyebrow="مسیر مشتری"
        wide
        voiceExpert={voiceExpert}
        voiceScreen="requests"
      >
        {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
        <CustomerRequestList
          requests={requests}
          onNew={() => {
            applyRequestForm(null);
            navigate(IDENTITY_WEB_ROUTES.customerRequestNew);
          }}
          onOpen={(id, draft) => {
            if (draft) {
              setEditingRequestId(id);
              navigate(IDENTITY_WEB_ROUTES.customerRequestEdit);
            } else {
              setViewingRequestId(id);
              navigate(IDENTITY_WEB_ROUTES.customerRequestDetail);
            }
          }}
        />
        <div className="account-actions">
          <button
            type="button"
            className="secondary"
            onClick={() => navigate(IDENTITY_WEB_ROUTES.customerHome)}
          >
            بازگشت به خانه
          </button>
        </div>
      </Shell>
    );
  }

  if (
    route === IDENTITY_WEB_ROUTES.customerRequestNew ||
    route === IDENTITY_WEB_ROUTES.customerRequestEdit
  ) {
    const isEdit = route === IDENTITY_WEB_ROUTES.customerRequestEdit;
    return (
      <Shell
        title={isEdit ? "ویرایش سفارش" : "ثبت سفارش"}
        description="سه مرحله روشن تا شروع جست‌وجوی قالیشویی مناسب."
        onAccount={() => navigate(IDENTITY_WEB_ROUTES.account)}
        eyebrow="مسیر مشتری"
        wide
      >
        <CustomerRequestWizard
          isEdit={isEdit}
          busy={busy}
          error={error}
          info={info}
          carpetType={carpetType}
          carpetCount={carpetCount}
          approximateSqm={approximateSqm}
          selectedServices={selectedServices}
          requestItems={requestItems}
          notes={notes}
          maxReturnWaitDays={maxReturnWaitDays}
          flowType={flowType}
          voiceExpert={voiceExpert}
          matchingPreference={matchingPreference}
          needPickup={needPickup}
          needDelivery={needDelivery}
          floorCount={floorCount}
          hasElevator={hasElevator}
          onCarpetType={setCarpetType}
          onCarpetCount={setCarpetCount}
          onApproximateSqm={setApproximateSqm}
          onToggleService={toggleService}
          onRequestItems={setRequestItems}
          onNotes={setNotes}
          onMaxReturnWaitDays={setMaxReturnWaitDays}
          onFlowType={setFlowType}
          onMatchingPreference={setMatchingPreference}
          onNeedPickup={setNeedPickup}
          onNeedDelivery={setNeedDelivery}
          onFloorCount={setFloorCount}
          onHasElevator={setHasElevator}
          onAssistantTranscribe={(audioBase64, audioMimeType) =>
            api.transcribeOrderAudio(audioBase64, audioMimeType)
          }
          onBack={() => navigate(IDENTITY_WEB_ROUTES.customerRequests)}
          onSaveDraft={() => {
            void run(async () => {
              const payload = requestPayload();
              const saved =
                isEdit && editingRequestId
                  ? await api.updateRequest(editingRequestId, payload)
                  : await api.createRequest(payload);
              applyRequestForm(saved);
              setInfo("پیش‌نویس ذخیره شد.");
              navigate(IDENTITY_WEB_ROUTES.customerRequests);
            });
          }}
          onSubmit={() => {
            void run(async () => {
              const payload = requestPayload();
              const currentProfile = profile ?? (await api.getProfile());
              setProfile(currentProfile);
              const draft =
                isEdit && editingRequestId
                  ? await api.updateRequest(editingRequestId, payload)
                  : await api.createRequest(payload);
              if (
                !currentProfile.fullName?.trim() ||
                !currentProfile.addressText?.trim()
              ) {
                applyRequestForm(draft);
                setProfileReturnRoute(IDENTITY_WEB_ROUTES.customerRequestEdit);
                setInfo(
                  "درخواست به‌صورت پیش‌نویس ذخیره شد. برای ارسال فقط نام و آدرس را کامل کنید.",
                );
                navigate(IDENTITY_WEB_ROUTES.customerProfile);
                return;
              }
              const submitted = await api.submitRequest(draft.id, flowType);
              applyRequestForm(submitted.request);
              setViewingRequestId(submitted.request.id);
              setInfo(submitted.message);
              navigate(IDENTITY_WEB_ROUTES.customerRequestDetail);
            });
          }}
        />
      </Shell>
    );
  }

  if (route === IDENTITY_WEB_ROUTES.customerRequestDetail) {
    const detail = requestDetail;
    const selectableOffers = offers.filter(
      (offer) => offer.status === "pending",
    );
    const orderedSelectableOffers = [...selectableOffers].sort((a, b) => {
      if (offerSort === "price")
        return (
          (a.estimatedPrice ?? Number.POSITIVE_INFINITY) -
          (b.estimatedPrice ?? Number.POSITIVE_INFINITY)
        );
      if (offerSort === "time")
        return (
          (a.typicalReturnDays ?? Number.POSITIVE_INFINITY) -
          (b.typicalReturnDays ?? Number.POSITIVE_INFINITY)
        );
      if (offerSort === "rating")
        return (b.ratingAvg ?? -1) - (a.ratingAvg ?? -1);
      return selectableOffers.indexOf(a) - selectableOffers.indexOf(b);
    });
    const selectedOffer = selectableOffers.find(
      (offer) => offer.id === selectedOfferId,
    );
    const isMatchingNow = detail
      ? ACTIVE_MATCHING_STATUSES.has(detail.status)
      : false;
    const offersAreReady = detail
      ? [
          "offered",
          "waiting_acceptance",
          "accepted",
          "contact_pending",
          "price_pending",
          "schedule_pending",
          "scheduled",
          "completed",
        ].includes(detail.status)
      : false;
    return (
      <Shell
        title="جزئیات درخواست"
        description="وضعیت تطبیق با قالیشویی‌ها و اقدام‌های بعدی را اینجا ببینید."
        onAccount={() => navigate(IDENTITY_WEB_ROUTES.account)}
        eyebrow="مسیر مشتری"
        voiceExpert={voiceExpert}
        voiceScreen="request_detail"
      >
        {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
        {info ? <StatusMessage kind="info">{info}</StatusMessage> : null}
        {detail ? <CustomerRequestProgress request={detail} /> : null}
        {detail ? (
          <section
            className={
              isMatchingNow
                ? "order-confirmation order-confirmation--searching"
                : "order-confirmation"
            }
            aria-label="سفارش ثبت‌شده"
          >
            <div className="order-search-hero" role="status" aria-live="polite">
              <div className="order-search-radar" aria-hidden="true">
                <span />
                <i />
              </div>
              <div>
                <span className="order-confirmation-kicker">
                  سفارش با موفقیت ثبت شد
                </span>
                <h2>
                  {isMatchingNow
                    ? "در حال جست‌وجوی قالیشویی مناسب"
                    : detail.status === "offered"
                      ? `${selectableOffers.length.toLocaleString("fa-IR")} پیشنهاد مناسب آماده است`
                      : statusLabel(detail.status)}
                </h2>
                <p>
                  {isMatchingNow
                    ? "سفارش شما میان قالیشویی‌های تأییدشده و فعال بررسی می‌شود؛ این صفحه خودکار به‌روز می‌شود."
                    : detail.status === "offered"
                      ? "قیمت، زمان تحویل و امتیاز هر قالیشویی آمادهٔ مقایسه است."
                      : "وضعیت فعلی و جزئیات کامل سفارش شما در ادامه آمده است."}
                </p>
                {isMatchingNow ? (
                  <div className="order-search-dots" aria-hidden="true">
                    <b />
                    <b />
                    <b />
                  </div>
                ) : null}
              </div>
            </div>
            <ol className="order-search-timeline" aria-label="مراحل جست‌وجو">
              <li className="completed">
                <span aria-hidden="true">✓</span>
                <strong>مشخصات سفارش بررسی شد</strong>
                <small>انجام شد</small>
              </li>
              <li className={isMatchingNow ? "active" : "completed"}>
                <span aria-hidden="true">{isMatchingNow ? "⌕" : "✓"}</span>
                <strong>یافتن قالیشویی‌های فعال</strong>
                <small>{isMatchingNow ? "در حال انجام" : "انجام شد"}</small>
              </li>
              <li className={offersAreReady ? "completed" : "pending"}>
                <span aria-hidden="true">{offersAreReady ? "✓" : "۳"}</span>
                <strong>دریافت قیمت و زمان</strong>
                <small>
                  {offersAreReady ? "پیشنهادها آماده‌اند" : "در انتظار"}
                </small>
              </li>
            </ol>
            {isMatchingNow ? (
              <div className="order-search-leave-note">
                <span aria-hidden="true">◉</span>
                می‌توانید از این صفحه خارج شوید؛ با آماده‌شدن پیشنهادها خبرتان
                می‌کنیم.
              </div>
            ) : null}
            <details
              className="order-confirmation-details"
              open={!isMatchingNow}
            >
              <summary>
                <span>جزئیات سفارش ثبت‌شده</span>
                <b aria-hidden="true">⌄</b>
              </summary>
              <div className="order-confirmation-summary">
                <header>
                  <div>
                    <span>خلاصه سفارش ثبت‌شده</span>
                    <strong>
                      {detail.items.length.toLocaleString("fa-IR")} گروه خدمت
                    </strong>
                  </div>
                  <span className={`status-pill status-${detail.status}`}>
                    {statusLabel(detail.status)}
                  </span>
                </header>
                <div className="order-confirmation-items">
                  {detail.items.length === 0 ? (
                    <article>
                      <span className="order-confirmation-item-index">…</span>
                      <div>
                        <strong>جزئیات اقلام در حال تکمیل است</strong>
                        <small>
                          وضعیت سفارش ثبت شده و اطلاعات این بخش به‌زودی کامل
                          می‌شود.
                        </small>
                      </div>
                    </article>
                  ) : (
                    detail.items.map((item, index) => (
                      <article key={`${item.itemType ?? "item"}-${index}`}>
                        <span className="order-confirmation-item-index">
                          {(index + 1).toLocaleString("fa-IR")}
                        </span>
                        <div>
                          <strong>
                            {ITEM_TYPE_LABELS_FA[item.itemType] ??
                              "نوع خدمت در حال تکمیل"}
                          </strong>
                          <small>
                            {orderItemCountLabel(item)} ·{" "}
                            {orderItemMeasurementLabel(item)}
                          </small>
                          <small>{orderItemServicesLabel(item)}</small>
                        </div>
                      </article>
                    ))
                  )}
                </div>
                <dl className="order-confirmation-facts">
                  <div>
                    <dt>روش انتخاب</dt>
                    <dd>
                      {
                        MATCHING_FLOW_LABELS[
                          detail.flowType ?? "auto_broadcast"
                        ]
                      }
                    </dd>
                  </div>
                  <div>
                    <dt>اولویت</dt>
                    <dd>
                      {MATCHING_PREFERENCE_LABELS[detail.matchingPreference]}
                    </dd>
                  </div>
                  <div>
                    <dt>ثبت سفارش</dt>
                    <dd>
                      {formatJalaliDateTime(
                        detail.submittedAt ?? detail.createdAt,
                      )}
                    </dd>
                  </div>
                </dl>
                {detail.wave2Bundle ? (
                  <div className="request-selected-priority" role="status">
                    <span>اولویت تکمیلی ثبت‌شدهٔ شما</span>
                    <strong>✓ {WAVE2_LABELS[detail.wave2Bundle]}</strong>
                    <small>{WAVE2_DESCRIPTIONS[detail.wave2Bundle]}</small>
                  </div>
                ) : null}
              </div>
            </details>
          </section>
        ) : (
          <StatusMessage kind="info">در حال بارگذاری جزئیات…</StatusMessage>
        )}

        {detail ? (
          <div className="request-item request-item--compact">
            <span>
              آخرین به‌روزرسانی: {formatJalaliDateTime(detail.updatedAt)}
            </span>
            {detail.notes ? (
              <div>
                <strong>توضیحات سفارش</strong>
                <span>{detail.notes}</span>
              </div>
            ) : null}
          </div>
        ) : null}

        {detail?.status === "offered" ? (
          <section className="offer-section" aria-label="پیشنهادهای قالیشویی">
            <div className="offer-section-head">
              <div>
                <span>پیشنهادهای آماده</span>
                <h2>پیشنهادها را مقایسه و انتخاب کنید</h2>
              </div>
              <strong>
                {selectableOffers.length.toLocaleString("fa-IR")} پیشنهاد
              </strong>
            </div>
            <div className="offer-expert-strip">
              <span aria-hidden="true">✦</span>
              <div>
                <strong>کارشناس همراه قالیشور</strong>
                <small>
                  قیمت، زمان، فاصله و امتیاز هر گزینه کنار هم آمده است؛ انتخاب
                  نهایی فقط با شماست.
                </small>
              </div>
            </div>
            <div
              className="offer-sort-controls"
              aria-label="مرتب‌سازی پیشنهادها"
            >
              {(
                [
                  ["recommended", "پیشنهاد قالیشور"],
                  ["price", "قیمت کمتر"],
                  ["time", "تحویل سریع‌تر"],
                  ["rating", "امتیاز بالاتر"],
                ] as const
              ).map(([value, label]) => (
                <button
                  type="button"
                  className={offerSort === value ? "selected" : ""}
                  aria-pressed={offerSort === value}
                  onClick={() => setOfferSort(value)}
                  key={value}
                >
                  {label}
                </button>
              ))}
            </div>
            <div
              className="offer-grid"
              role="radiogroup"
              aria-label="انتخاب قالیشویی"
            >
              {orderedSelectableOffers.map((offer) => {
                const recommended = selectableOffers[0]?.id === offer.id;
                const selected = selectedOfferId === offer.id;
                return (
                  <article
                    className={`offer-card${recommended ? " offer-card-featured" : ""}${selected ? " offer-card-selected" : ""}`}
                    key={offer.id}
                  >
                    {recommended ? (
                      <div className="offer-recommendation-band">
                        <span>✓ پیشنهاد متعادل قالیشور</span>
                        <small>بهترین ترکیب</small>
                      </div>
                    ) : null}
                    <header>
                      <div>
                        <span
                          className="offer-business-mark"
                          aria-hidden="true"
                        >
                          ◈
                        </span>
                        <span>
                          <strong>{offer.businessName} ✓</strong>
                          <small>
                            قالیشویی تأییدشده
                            {offer.ratingCount > 0
                              ? ` · ${offer.ratingCount.toLocaleString("fa-IR")} نظر`
                              : ""}
                          </small>
                        </span>
                      </div>
                      {offer.ratingAvg != null ? (
                        <span className="offer-rating">
                          ★ {offer.ratingAvg.toLocaleString("fa-IR")}
                        </span>
                      ) : null}
                    </header>
                    <div className="offer-facts">
                      {offer.distanceKm != null ? (
                        <span>
                          <small>فاصله</small>
                          {offer.distanceKm.toLocaleString("fa-IR")} کیلومتر
                        </span>
                      ) : null}
                      {offer.estimatedPrice != null ? (
                        <span>
                          <small>
                            {offer.autoQuote
                              ? "قیمت محاسبه‌شده"
                              : "قیمت برآوردی"}
                          </small>
                          {offer.estimatedPrice.toLocaleString("fa-IR")} ریال
                        </span>
                      ) : null}
                      {offer.typicalReturnDays != null ? (
                        <span>
                          <small>زمان معمول</small>
                          {offer.typicalReturnDays.toLocaleString("fa-IR")} روز
                        </span>
                      ) : null}
                      {offer.proposedPickupAt ? (
                        <span>
                          <small>
                            {offer.autoSchedule
                              ? "جمع‌آوری خودکار"
                              : "جمع‌آوری پیشنهادی"}
                          </small>
                          {formatJalaliDateTime(offer.proposedPickupAt)}
                        </span>
                      ) : null}
                      {offer.proposedReturnAt ? (
                        <span>
                          <small>بازگشت پیشنهادی</small>
                          {formatJalaliDateTime(offer.proposedReturnAt)}
                        </span>
                      ) : null}
                    </div>
                    {(offer.badges ?? []).length > 0 ? (
                      <div className="offer-badges">
                        {(offer.badges ?? []).map((badge) => (
                          <span key={badge}>{formatOfferBadge(badge)}</span>
                        ))}
                      </div>
                    ) : null}
                    {recommended &&
                    (offer.recommendationReasons ?? []).length > 0 ? (
                      <div className="offer-reasons">
                        <strong>چرا این گزینه پیشنهاد شده؟</strong>
                        {(offer.recommendationReasons ?? []).map((reason) => (
                          <span key={reason}>✓ {reason}</span>
                        ))}
                      </div>
                    ) : null}
                    {offer.estimatedPrice != null ? (
                      <details className="offer-cost-breakdown">
                        <summary>ریز هزینه‌های برآوردی</summary>
                        {(offer.priceLines ?? []).map((line, lineIndex) => (
                          <span key={`${line.serviceKey}-${lineIndex}`}>
                            <b>
                              {SERVICE_LABELS_FA[
                                line.serviceKey as ServiceCatalogKey
                              ] ?? line.serviceKey}
                            </b>
                            <em>{line.amount.toLocaleString("fa-IR")} ریال</em>
                          </span>
                        ))}
                        {(offer.logisticsCosts?.pickup ?? 0) > 0 ? (
                          <span>
                            <b>جمع‌آوری</b>
                            <em>
                              {offer.logisticsCosts.pickup.toLocaleString(
                                "fa-IR",
                              )}{" "}
                              ریال
                            </em>
                          </span>
                        ) : null}
                        {(offer.logisticsCosts?.delivery ?? 0) > 0 ? (
                          <span>
                            <b>تحویل</b>
                            <em>
                              {offer.logisticsCosts.delivery.toLocaleString(
                                "fa-IR",
                              )}{" "}
                              ریال
                            </em>
                          </span>
                        ) : null}
                        {(offer.logisticsCosts?.floorCarry ?? 0) > 0 ? (
                          <span>
                            <b>هزینه طبقات</b>
                            <em>
                              {offer.logisticsCosts.floorCarry.toLocaleString(
                                "fa-IR",
                              )}{" "}
                              ریال
                            </em>
                          </span>
                        ) : null}
                      </details>
                    ) : null}
                    <button
                      type="button"
                      className={
                        selected
                          ? "offer-select-button selected"
                          : "offer-select-button"
                      }
                      role="radio"
                      aria-checked={selected}
                      disabled={busy}
                      onClick={() => setSelectedOfferId(offer.id)}
                    >
                      {selected ? "✓ انتخاب شد" : "انتخاب این قالیشویی"}
                    </button>
                  </article>
                );
              })}
            </div>
            {selectableOffers.length === 0 ? (
              <StatusMessage kind="info">
                پیشنهاد فعالی برای انتخاب نیست.
              </StatusMessage>
            ) : null}
            {selectableOffers.length > 0 ? (
              <div className="offer-confirm-dock" aria-live="polite">
                <span>
                  {selectedOffer
                    ? `${selectedOffer.businessName} انتخاب شده است`
                    : "برای ادامه یک قالیشویی را انتخاب کنید"}
                </span>
                <button
                  type="button"
                  className="cta-primary"
                  disabled={busy || !selectedOffer}
                  onClick={() => {
                    if (!selectedOffer) return;
                    void run(async () => {
                      const updated = await api.selectOffer(
                        detail.id,
                        selectedOffer.id,
                      );
                      setRequestDetail(updated);
                      setSelectedOfferId(null);
                      setInfo(
                        `${selectedOffer.businessName} انتخاب شد. منتظر تأیید جمع‌آوری بمانید.`,
                      );
                    });
                  }}
                >
                  {busy
                    ? "در حال ثبت انتخاب…"
                    : selectedOffer
                      ? `تأیید ${selectedOffer.businessName}`
                      : "تأیید انتخاب"}
                </button>
              </div>
            ) : null}
          </section>
        ) : null}

        {detail?.status === "matching" &&
        detail.flowType === "auto_broadcast" ? (
          <div className="account-actions">
            {detail.wave2Bundle ? (
              <div
                className="wave2-confirmation"
                role="status"
                aria-live="polite"
              >
                <span aria-hidden="true">✓</span>
                <div>
                  <strong>
                    انتخاب «{WAVE2_LABELS[detail.wave2Bundle]}» ثبت شد
                  </strong>
                  <small>
                    جست‌وجوی دوباره در حال انجام است. می‌توانید همین انتخاب را
                    پایین صفحه ببینید یا آن را تغییر دهید.
                  </small>
                </div>
              </div>
            ) : (
              <p className="lead">
                هیچ قالیشویی نپذیرفت. اولویت ادامهٔ جست‌وجو را انتخاب کنید:
              </p>
            )}
            <div className="wave2-choices" aria-label="اولویت ادامه جست‌وجو">
              {WAVE2_BUNDLES.map((bundle) => (
                <button
                  key={bundle}
                  type="button"
                  className={
                    detail.wave2Bundle === bundle
                      ? "wave2-choice selected"
                      : "wave2-choice"
                  }
                  aria-pressed={detail.wave2Bundle === bundle}
                  disabled={busy}
                  onClick={() => {
                    if (detail.wave2Bundle === bundle) return;
                    void run(async () => {
                      const updated = await api.applyWave2(detail.id, bundle);
                      setRequestDetail(updated);
                      setInfo(
                        `انتخاب «${WAVE2_LABELS[bundle]}» با موفقیت ثبت شد و جست‌وجوی دوباره آغاز شد.`,
                      );
                    });
                  }}
                >
                  <span className="wave2-choice-icon" aria-hidden="true">
                    {detail.wave2Bundle === bundle ? "✓" : "○"}
                  </span>
                  <span>
                    <strong>{WAVE2_LABELS[bundle]}</strong>
                    <small>{WAVE2_DESCRIPTIONS[bundle]}</small>
                  </span>
                  {detail.wave2Bundle === bundle ? <b>انتخاب شما</b> : null}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {detail?.status === "matching" &&
        detail.flowType === "curated_offers" ? (
          <StatusMessage kind="info">
            در حال یافتن پیشنهادهای جدید…
          </StatusMessage>
        ) : null}

        {detail?.status === "waiting_acceptance" ? (
          <StatusMessage kind="info">منتظر پذیرش قالیشویی هستیم.</StatusMessage>
        ) : null}

        {detail &&
        [
          "accepted",
          "contact_pending",
          "price_pending",
          "schedule_pending",
          "scheduled",
          "completed",
        ].includes(detail.status) ? (
          <AssignmentPanel
            requestId={detail.id}
            viewer="customer"
            api={api}
            busy={busy}
            run={run}
            onInfo={setInfo}
            onRevision={() => setRevision((value) => value + 1)}
          />
        ) : null}

        <div className="account-actions">
          {detail &&
          detail.status !== "completed" &&
          detail.status !== "cancelled" ? (
            <button
              type="button"
              className="danger"
              disabled={busy}
              onClick={() => {
                if (!window.confirm("آیا از لغو این درخواست مطمئن هستید؟"))
                  return;
                void run(async () => {
                  const updated = await api.cancelRequest(detail.id);
                  setRequestDetail(updated);
                  setInfo("درخواست لغو شد.");
                  setRevision((value) => value + 1);
                });
              }}
            >
              لغو این درخواست
            </button>
          ) : null}
          <button
            type="button"
            className="secondary"
            onClick={() => navigate(IDENTITY_WEB_ROUTES.customerRequests)}
          >
            بازگشت به فهرست
          </button>
        </div>
      </Shell>
    );
  }

  if (route === IDENTITY_WEB_ROUTES.businessSelect) {
    const activeMemberships = snapshot.memberships.filter(
      (item) => item.status === "active" && item.businessStatus === "active",
    );
    const pendingMemberships = snapshot.memberships.filter(
      (item) => item.status === "active" && item.businessStatus === "pending",
    );
    return (
      <Shell
        title="انتخاب قالیشویی"
        description="کدام کسب‌وکار را می‌خواهید مدیریت کنید؟"
        onAccount={() => navigate(IDENTITY_WEB_ROUTES.account)}
        eyebrow="مسیر قالیشویی"
      >
        <div className="business-list">
          {activeMemberships.map((membership) => (
            <button
              type="button"
              className="business-item"
              key={membership.id}
              onClick={() => selectBusiness(membership)}
            >
              <strong>{membership.businessName}</strong>
              <span>
                {membership.isPrimaryOwner ? "مالک اصلی" : "عضو کسب‌وکار"}
              </span>
              <small>وضعیت: {membership.businessStatus}</small>
            </button>
          ))}
        </div>
        {info ? <StatusMessage kind="info">{info}</StatusMessage> : null}
        {pendingMemberships.map((membership) => (
          <div className="business-pending-card" key={membership.id}>
            <strong>{membership.businessName}</strong>
            <span>
              درخواست همکاری شما ثبت شده و در انتظار تأیید مدیران قالیشور است.
            </span>
            <small>
              پس از فعال‌سازی، دسترسی مدیریت درخواست‌ها به‌صورت خودکار باز
              می‌شود.
            </small>
          </div>
        ))}
        {activeMemberships.length === 0 && pendingMemberships.length === 0 ? (
          <StatusMessage kind="info">
            هنوز قالیشویی فعالی ندارید؛ می‌توانید درخواست همکاری ثبت کنید.
          </StatusMessage>
        ) : null}
        <div className="account-actions">
          <button
            type="button"
            onClick={() => navigate(IDENTITY_WEB_ROUTES.businessCreate)}
          >
            اعلام آمادگی همکاری
          </button>
        </div>
      </Shell>
    );
  }

  if (route === IDENTITY_WEB_ROUTES.businessCreate) {
    return (
      <Shell
        title="اعلام آمادگی همکاری"
        description="مشخصات قالیشویی را ثبت کنید؛ فعالیت پس از بررسی و تأیید مدیران قالیشور آغاز می‌شود."
        onAccount={() => navigate(IDENTITY_WEB_ROUTES.account)}
        eyebrow="مسیر قالیشویی"
      >
        <form className="identity-form" onSubmit={createBusiness}>
          <label>
            نام قالیشویی
            <input
              value={businessName}
              onChange={(event) => setBusinessName(event.target.value)}
              minLength={2}
              maxLength={180}
              required
            />
          </label>
          {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
          <button type="submit" disabled={busy}>
            {busy ? "در حال ثبت…" : "ثبت درخواست همکاری"}
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() =>
              navigate(
                snapshot.memberships.length > 0
                  ? IDENTITY_WEB_ROUTES.businessSelect
                  : IDENTITY_WEB_ROUTES.home,
              )
            }
          >
            بازگشت
          </button>
        </form>
      </Shell>
    );
  }

  if (route === IDENTITY_WEB_ROUTES.passwordChange) {
    return (
      <Shell
        title="تغییر رمز عبور"
        description="برای ادامه باید رمز موقت را عوض کنید و رمز شخصی خود را بسازید."
        eyebrow="امنیت حساب"
      >
        {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
        {info ? <StatusMessage kind="info">{info}</StatusMessage> : null}
        <form
          className="identity-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (newPassword !== confirmPassword) {
              setError("تکرار رمز با رمز جدید یکسان نیست.");
              return;
            }
            void run(async () => {
              await api.changePassword({ newPassword });
              setMustChangePassword(false);
              setNewPassword("");
              setConfirmPassword("");
              setInfo("رمز عبور با موفقیت به‌روز شد.");
              navigate(
                routeAfterAuthentication(
                  snapshot.memberships,
                  sessionStore.getSelectedBusinessId(),
                  loginIntent ?? "business",
                ),
              );
            });
          }}
        >
          <PasswordField
            label="رمز جدید"
            value={newPassword}
            visible={showPassword}
            onChange={setNewPassword}
            onToggle={() => setShowPassword((value) => !value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
          <PasswordField
            label="تکرار رمز جدید"
            value={confirmPassword}
            visible={showConfirmPassword}
            onChange={setConfirmPassword}
            onToggle={() => setShowConfirmPassword((value) => !value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
          <button
            type="submit"
            disabled={busy || newPassword.trim().length < 8}
          >
            {busy ? "در حال ذخیره…" : "ذخیره رمز جدید"}
          </button>
          <button type="button" className="secondary" onClick={logoutDevice}>
            خروج از این دستگاه
          </button>
        </form>
      </Shell>
    );
  }

  if (route === IDENTITY_WEB_ROUTES.account) {
    return (
      <Shell
        title="حساب کاربری"
        description={`شماره هویت: ${snapshot.user?.mobileE164 ?? "—"}`}
        voiceExpert={loginIntent === "customer" ? voiceExpert : undefined}
        voiceScreen={loginIntent === "customer" ? "account" : undefined}
      >
        {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
        {info ? <StatusMessage kind="info">{info}</StatusMessage> : null}
        <AccountSecurityPanel
          api={api}
          busy={busy}
          onBusy={setBusy}
          onInfo={setInfo}
          onError={setError}
        />
        <div className="account-actions">
          <button
            type="button"
            onClick={() =>
              navigate(
                routeAfterAuthentication(
                  snapshot.memberships,
                  snapshot.selectedBusinessId,
                  loginIntent,
                ),
              )
            }
          >
            بازگشت به فضای فعال
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() =>
              navigate(
                snapshot.memberships.length > 0
                  ? IDENTITY_WEB_ROUTES.businessHome
                  : IDENTITY_WEB_ROUTES.loginBusiness,
              )
            }
          >
            ورود / فضای قالیشویی
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => navigate(IDENTITY_WEB_ROUTES.customerProfile)}
          >
            پروفایل مشتری
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => navigate(IDENTITY_WEB_ROUTES.downloads)}
          >
            دانلود اپ اندروید
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => navigate(IDENTITY_WEB_ROUTES.partners)}
          >
            صفحه عمومی همکاری
          </button>
          <button type="button" className="secondary" onClick={logoutDevice}>
            خروج از این دستگاه
          </button>
          <button
            type="button"
            className="danger"
            onClick={() =>
              void run(async () => {
                await api.logoutAll();
                clearLoginIntent(window.localStorage);
                setLoginIntent(null);
                setProfile(null);
                setProfileReturnRoute(null);
                setMustChangePassword(false);
                navigate(IDENTITY_WEB_ROUTES.home);
              })
            }
          >
            خروج از همه دستگاه‌ها
          </button>
        </div>
      </Shell>
    );
  }

  if (route === IDENTITY_WEB_ROUTES.businessInbox) {
    const businessId = sessionStore.getSelectedBusinessId();
    return (
      <Shell
        title="صندوق درخواست‌ها"
        description="پیشنهادهای در انتظار پذیرش را اینجا ببینید و بپذیرید یا رد کنید."
        onAccount={() => navigate(IDENTITY_WEB_ROUTES.account)}
        eyebrow="مسیر قالیشویی"
      >
        {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
        {info ? <StatusMessage kind="info">{info}</StatusMessage> : null}
        {businessMessages.length > 0 ? (
          <section className="business-platform-messages">
            <h2>پیام‌های مدیریت پلتفرم</h2>
            {businessMessages.map((message) => (
              <article
                className={`business-platform-message${message.readAt === null ? " is-unread" : ""}`}
                key={message.id}
              >
                <div>
                  <strong>{message.title}</strong>
                  <small>
                    {message.senderName} ·{" "}
                    {formatJalaliDateTime(message.createdAt)}
                  </small>
                </div>
                <p>{message.body}</p>
                {message.readAt === null ? (
                  <button
                    type="button"
                    className="secondary"
                    disabled={busy || businessId === null}
                    onClick={() => {
                      if (businessId === null) return;
                      void run(async () => {
                        await api.readBusinessDirectMessage(
                          businessId,
                          message.id,
                        );
                        setInfo("پیام به‌عنوان خوانده‌شده ثبت شد.");
                        setRevision((value) => value + 1);
                      });
                    }}
                  >
                    خواندم
                  </button>
                ) : (
                  <small>خوانده‌شده</small>
                )}
              </article>
            ))}
          </section>
        ) : null}
        <div className="request-list">
          {inbox.map((item) => (
            <div className="request-item" key={item.offerId}>
              <strong>
                {ITEM_TYPE_LABELS_FA[item.request.carpetType] ??
                  item.request.carpetType}{" "}
                · {item.request.carpetCount.toLocaleString("fa-IR")}{" "}
                {countUnitForItem(item.request.carpetType)}
              </strong>
              <span>
                متراژ تقریبی:{" "}
                {item.request.approximateSqm.toLocaleString("fa-IR")} مترمربع
              </span>
              {item.request.maxReturnWaitDays != null ? (
                <span>
                  سقف انتظار برگشت مشتری:{" "}
                  {item.request.maxReturnWaitDays.toLocaleString("fa-IR")} روز
                </span>
              ) : null}
              <span>موج: {item.wave.toLocaleString("fa-IR")}</span>
              <div className="account-actions">
                <button
                  type="button"
                  disabled={busy || businessId === null}
                  onClick={() => {
                    if (businessId === null) return;
                    void run(async () => {
                      await api.acceptOffer(businessId, item.offerId);
                      setInfo(
                        "درخواست پذیرفته شد. اکنون هماهنگی تماس را انجام دهید.",
                      );
                      setActiveAssignmentId(item.serviceRequestId);
                      navigate(IDENTITY_WEB_ROUTES.businessActive);
                      setRevision((value) => value + 1);
                    });
                  }}
                >
                  پذیرش
                </button>
                <button
                  type="button"
                  className="secondary"
                  disabled={busy || businessId === null}
                  onClick={() => {
                    if (businessId === null) return;
                    void run(async () => {
                      await api.rejectOffer(businessId, item.offerId);
                      setInfo("پیشنهاد رد شد.");
                      setRevision((value) => value + 1);
                    });
                  }}
                >
                  رد
                </button>
              </div>
            </div>
          ))}
        </div>
        {inbox.length === 0 ? (
          <StatusMessage kind="info">درخواستی در انتظار نیست.</StatusMessage>
        ) : null}
        <div className="account-actions">
          <button
            type="button"
            className="secondary"
            onClick={() => navigate(IDENTITY_WEB_ROUTES.businessHome)}
          >
            بازگشت
          </button>
        </div>
      </Shell>
    );
  }

  if (route === IDENTITY_WEB_ROUTES.businessActive) {
    const businessId = sessionStore.getSelectedBusinessId();
    if (activeAssignmentId && businessId) {
      return (
        <Shell
          title="هماهنگی با مشتری"
          description="شماره مشتری پس از پذیرش نمایش داده می‌شود. زمان جمع‌آوری را ثبت کنید."
          onAccount={() => navigate(IDENTITY_WEB_ROUTES.account)}
          eyebrow="مسیر قالیشویی"
        >
          {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
          {info ? <StatusMessage kind="info">{info}</StatusMessage> : null}
          <AssignmentPanel
            requestId={activeAssignmentId}
            viewer="business"
            api={api}
            busy={busy}
            run={run}
            onInfo={setInfo}
            onRevision={() => setRevision((value) => value + 1)}
            businessId={businessId}
          />
          <div className="account-actions">
            <button
              type="button"
              className="secondary"
              onClick={() => setActiveAssignmentId(null)}
            >
              بازگشت به فهرست کارها
            </button>
          </div>
        </Shell>
      );
    }
    return (
      <Shell
        title="کارهای فعال"
        description="درخواست‌های پذیرفته‌شده برای هماهنگی تماس و زمان‌بندی."
        onAccount={() => navigate(IDENTITY_WEB_ROUTES.account)}
        eyebrow="مسیر قالیشویی"
      >
        {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
        <div className="request-list">
          {activeJobs.map((item) => (
            <div className="request-item" key={item.serviceRequestId}>
              <strong>
                {ITEM_TYPE_LABELS_FA[item.carpetType] ?? item.carpetType} ·{" "}
                {item.carpetCount.toLocaleString("fa-IR")}{" "}
                {countUnitForItem(item.carpetType)}
              </strong>
              <span className={`status-pill status-${item.status}`}>
                {statusLabel(item.status)}
              </span>
              <span>شهر مشتری: {item.customerCity ?? "—"}</span>
              <button
                type="button"
                onClick={() => setActiveAssignmentId(item.serviceRequestId)}
              >
                هماهنگی
              </button>
            </div>
          ))}
        </div>
        {activeJobs.length === 0 ? (
          <StatusMessage kind="info">کار فعالی نیست.</StatusMessage>
        ) : null}
        <div className="account-actions">
          <button
            type="button"
            className="secondary"
            onClick={() => navigate(IDENTITY_WEB_ROUTES.businessHome)}
          >
            بازگشت
          </button>
        </div>
      </Shell>
    );
  }

  if (route === IDENTITY_WEB_ROUTES.businessMessages) {
    const businessId = sessionStore.getSelectedBusinessId();
    return (
      <Shell
        title="پیام‌های قالیشویی"
        description="گفت‌وگوی زنده با تیم، مشتریان مرتبط و مدیران پلتفرم."
        onAccount={() => navigate(IDENTITY_WEB_ROUTES.account)}
        eyebrow="مسیر قالیشویی"
      >
        <nav className="business-page-location" aria-label="مسیر صفحه">
          <span>داشبورد قالیشویی ← پیام‌ها و اعلان‌ها</span>
          <div>
            <button
              type="button"
              onClick={() => navigate(IDENTITY_WEB_ROUTES.businessHome)}
            >
              بازگشت به داشبورد
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() => navigate(IDENTITY_WEB_ROUTES.businessConsole)}
            >
              کنسول مدیریت
            </button>
          </div>
        </nav>
        <MessagingCenter api={api} mode="business" businessId={businessId} />
      </Shell>
    );
  }

  if (
    route === IDENTITY_WEB_ROUTES.businessProfile ||
    route === IDENTITY_WEB_ROUTES.businessConsoleProfile
  ) {
    const businessId = sessionStore.getSelectedBusinessId();
    const isConsole = route === IDENTITY_WEB_ROUTES.businessConsoleProfile;
    return (
      <Shell
        title={isConsole ? "پروفایل عملیاتی" : "مکان و نرخ خدمت"}
        description="مختصات و شعاع خدمت برای matching جغرافیایی. نرخ‌های کامل در کنسول → خدمات و نرخ‌ها."
        onAccount={() => navigate(IDENTITY_WEB_ROUTES.account)}
        eyebrow={isConsole ? "کنسول مدیریت" : "مسیر قالیشویی"}
      >
        {isConsole ? (
          <div
            className="account-actions"
            style={{ marginBottom: "1rem", flexWrap: "wrap" }}
          >
            <button
              type="button"
              className="secondary"
              onClick={() =>
                navigate(IDENTITY_WEB_ROUTES.businessConsoleReports)
              }
            >
              گزارشات
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() => navigate(IDENTITY_WEB_ROUTES.businessConsoleRates)}
            >
              خدمات و نرخ‌ها
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() =>
                navigate(IDENTITY_WEB_ROUTES.businessConsoleLogistics)
              }
            >
              حمل و طبقات
            </button>
            <button
              type="button"
              onClick={() =>
                navigate(IDENTITY_WEB_ROUTES.businessConsoleProfile)
              }
            >
              پروفایل عملیاتی
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() => navigate(IDENTITY_WEB_ROUTES.businessHome)}
            >
              داشبورد
            </button>
          </div>
        ) : null}
        <form
          className="identity-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!businessId) return;
            void run(async () => {
              await api.updateBusinessProfile(businessId, {
                city: bizCity.trim() || null,
                addressText: bizAddress.trim() || null,
                lat: bizLat.trim() === "" ? null : Number(bizLat),
                lng: bizLng.trim() === "" ? null : Number(bizLng),
                serviceRadiusKm: Number(bizRadius) || 15,
                typicalReturnDays: Number(bizTypicalReturnDays) || 5,
              });
              if (!isConsole) {
                await api.upsertRateCards(businessId, {
                  items: [
                    {
                      carpetType: "handmade",
                      serviceKey: "washing",
                      pricePerSqm: Number(rateWashing) || 0,
                      minPrice: 0,
                      currency: "IRR",
                    },
                    {
                      carpetType: "machine_made",
                      serviceKey: "washing",
                      pricePerSqm: Math.round((Number(rateWashing) || 0) * 0.8),
                      minPrice: 0,
                      currency: "IRR",
                    },
                  ],
                });
              }
              setInfo(
                isConsole
                  ? "پروفایل عملیاتی ذخیره شد."
                  : "پروفایل مکانی و نرخ ذخیره شد.",
              );
            });
          }}
        >
          <label>
            شهر
            <input
              value={bizCity}
              onChange={(event) => setBizCity(event.target.value)}
            />
          </label>
          <label>
            آدرس
            <textarea
              value={bizAddress}
              onChange={(event) => setBizAddress(event.target.value)}
              rows={2}
            />
          </label>
          <label>
            عرض جغرافیایی
            <input
              value={bizLat}
              onChange={(event) => setBizLat(event.target.value)}
              placeholder="35.715"
            />
          </label>
          <label>
            طول جغرافیایی
            <input
              value={bizLng}
              onChange={(event) => setBizLng(event.target.value)}
              placeholder="51.405"
            />
          </label>
          <label>
            شعاع خدمت (کیلومتر)
            <input
              value={bizRadius}
              onChange={(event) => setBizRadius(event.target.value)}
            />
          </label>
          <label>
            زمان معمول برگشت سفارش (روز)
            <input
              type="number"
              min={1}
              max={14}
              step={1}
              value={bizTypicalReturnDays}
              onChange={(event) => setBizTypicalReturnDays(event.target.value)}
              required
            />
          </label>
          {!isConsole ? (
            <label>
              نرخ شستشوی دستباف (ریال/مترمربع)
              <input
                value={rateWashing}
                onChange={(event) => setRateWashing(event.target.value)}
              />
            </label>
          ) : (
            <StatusMessage kind="info">
              برای تعریف نرخ کامل به «خدمات و نرخ‌ها» بروید.
            </StatusMessage>
          )}
          {info ? <StatusMessage kind="info">{info}</StatusMessage> : null}
          {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
          <button type="submit" disabled={busy || !businessId}>
            {busy ? "در حال ذخیره…" : "ذخیره"}
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() =>
              navigate(
                isConsole
                  ? IDENTITY_WEB_ROUTES.businessConsole
                  : IDENTITY_WEB_ROUTES.businessHome,
              )
            }
          >
            بازگشت
          </button>
        </form>
      </Shell>
    );
  }

  const consoleBusinessId = sessionStore.getSelectedBusinessId();
  if (route === IDENTITY_WEB_ROUTES.businessConsole && consoleBusinessId) {
    return (
      <BusinessConsoleHub
        context={context}
        onNavigate={navigate}
        Shell={Shell}
        onAccount={() => navigate(IDENTITY_WEB_ROUTES.account)}
      />
    );
  }
  if (
    route === IDENTITY_WEB_ROUTES.businessConsoleReports &&
    consoleBusinessId
  ) {
    return (
      <BusinessConsoleReportsPage
        businessId={consoleBusinessId}
        context={context}
        api={api}
        onNavigate={navigate}
        Shell={Shell}
        onAccount={() => navigate(IDENTITY_WEB_ROUTES.account)}
      />
    );
  }
  if (route === IDENTITY_WEB_ROUTES.businessConsoleRates && consoleBusinessId) {
    return (
      <BusinessConsoleRatesPage
        businessId={consoleBusinessId}
        context={context}
        api={api}
        onNavigate={navigate}
        Shell={Shell}
        onAccount={() => navigate(IDENTITY_WEB_ROUTES.account)}
      />
    );
  }
  if (
    route === IDENTITY_WEB_ROUTES.businessConsoleLogistics &&
    consoleBusinessId
  ) {
    return (
      <BusinessConsoleLogisticsPage
        businessId={consoleBusinessId}
        context={context}
        api={api}
        onNavigate={navigate}
        Shell={Shell}
        onAccount={() => navigate(IDENTITY_WEB_ROUTES.account)}
      />
    );
  }
  if (route === IDENTITY_WEB_ROUTES.businessConsoleTeam && consoleBusinessId) {
    return (
      <BusinessConsoleTeamPage
        businessId={consoleBusinessId}
        api={api}
        onNavigate={navigate}
        Shell={Shell}
        onAccount={() => navigate(IDENTITY_WEB_ROUTES.account)}
      />
    );
  }
  if (route === IDENTITY_WEB_ROUTES.businessConsoleFleet && consoleBusinessId) {
    return (
      <BusinessConsoleFleetPage
        businessId={consoleBusinessId}
        context={context}
        api={api}
        onNavigate={navigate}
        Shell={Shell}
        onAccount={() => navigate(IDENTITY_WEB_ROUTES.account)}
      />
    );
  }
  if (
    route === IDENTITY_WEB_ROUTES.businessConsoleSettings &&
    consoleBusinessId
  ) {
    return (
      <BusinessConsoleSettingsPage
        businessId={consoleBusinessId}
        context={context}
        api={api}
        onNavigate={navigate}
        Shell={Shell}
        onAccount={() => navigate(IDENTITY_WEB_ROUTES.account)}
      />
    );
  }

  return (
    <Shell
      title={context?.business.name ?? "فضای قالیشویی"}
      description="داشبورد اولیه کسب‌وکار. درخواست‌های مشتری را از صندوق پذیرش مدیریت کنید."
      onAccount={() => navigate(IDENTITY_WEB_ROUTES.account)}
      eyebrow="مسیر قالیشویی"
    >
      {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
      {context ? (
        <div className="context-panel">
          <strong>{context.business.name}</strong>
          <span>وضعیت کسب‌وکار: {context.business.status}</span>
          <span>
            نقش‌ها: {context.membership.roleKeys.join("، ") || "بدون نقش"}
          </span>
          <span>
            تعداد قابلیت‌های مؤثر:{" "}
            {context.effectiveCapabilities.length.toLocaleString("fa-IR")}
          </span>
        </div>
      ) : (
        <StatusMessage kind="info">
          اطلاعات کسب‌وکار در حال دریافت است…
        </StatusMessage>
      )}
      <button
        type="button"
        className="business-message-hub"
        onClick={() => navigate(IDENTITY_WEB_ROUTES.businessMessages)}
        aria-label={`پیام‌ها و اعلان‌ها${businessUnreadMessages ? `، ${businessUnreadMessages.toLocaleString("fa-IR")} پیام خوانده‌نشده` : ""}`}
      >
        <span className="business-message-hub__icon" aria-hidden="true">
          🔔
        </span>
        <span className="business-message-hub__text">
          <strong>پیام‌ها و اعلان‌ها</strong>
          <small>
            {businessUnreadMessages
              ? "پیام خوانده‌نشده دارید"
              : "همه پیام‌ها خوانده شده‌اند"}
          </small>
        </span>
        {businessUnreadMessages ? (
          <b>{businessUnreadMessages.toLocaleString("fa-IR")}</b>
        ) : (
          <em>مشاهده</em>
        )}
      </button>
      <div className="account-actions">
        <button
          type="button"
          onClick={() => navigate(IDENTITY_WEB_ROUTES.businessConsole)}
        >
          کنسول مدیریت
        </button>
        <button
          type="button"
          onClick={() => navigate(IDENTITY_WEB_ROUTES.businessInbox)}
        >
          صندوق درخواست‌ها
        </button>
        <button
          type="button"
          onClick={() => navigate(IDENTITY_WEB_ROUTES.businessActive)}
        >
          کارهای فعال / هماهنگی
        </button>
        <button
          type="button"
          className="secondary"
          onClick={() => navigate(IDENTITY_WEB_ROUTES.businessConsoleRates)}
        >
          خدمات و نرخ‌ها
        </button>
        <button
          type="button"
          className="secondary"
          onClick={() => navigate(IDENTITY_WEB_ROUTES.businessProfile)}
        >
          مکان سریع
        </button>
      </div>
    </Shell>
  );
}
